import { feedbackTranslations } from './translations-feedback';
import { customTranslations } from './translations-custom';

// Helper function to merge translations
const mergeTranslations = (base, ...additionsList) => {
  const result = JSON.parse(JSON.stringify(base));

  additionsList.forEach((additions) => {
    Object.keys(additions).forEach((lang) => {
      if (!result[lang]) {
        result[lang] = { translation: {} };
      }

      // Deep merge the translations
      result[lang].translation = {
        ...result[lang].translation,
        ...additions[lang],
      };
    });
  });

  return result;
};

// Base translations
const baseResources = {
  en: {
    translation: {
      coach: {
        landing: {
          title: 'Your Personal Leadership Coach',
          title1: 'Your Personal',
          title2: 'Leadership Coach',
          subtitle: 'in Your Slack',
          description:
            'Get personalized coaching, practice difficult conversations, and receive immediate feedback to become a better leader.',
          startTrial: 'Start Free Trial',
          requestAccess: {
            title: 'Request access',
            subtitle:
              'Enter your email to get started with our AI leadership coach',
            button: 'Request access',
            inputPlaceholder: 'Enter your email',
          },
          features: {
            personalCoach: {
              title: 'Personal AI Coach',
              description:
                'Get personalized coaching from an AI that understands your specific challenges and goals.',
            },
            realScenarios: {
              title: 'Real Scenarios',
              description:
                'Practice with realistic scenarios tailored to your experience level and current situation.',
            },
            immediateFeedback: {
              title: 'Immediate Feedback',
              description:
                'Receive instant, actionable feedback to improve your leadership skills after each session.',
            },
          },
        },
        categories: {
          title: "What's top of mind for you?",
          subtitle:
            'Access personalized coaching and guidance for managing your team',
          nameDialog: {
            title: "Let's get started",
            inputPlaceholder: 'Enter your name',
            button: 'Continue',
          },
        },
        chat: {
          sidebar: {
            home: 'Home',
          },
          typing: 'AI Coach is typing...',
          inputPlaceholder: 'Type your message...',
          establishConnection: 'Establishing secure connection...',
          prepareSession: 'Preparing your personalized practice session...',
          sendButton: 'Send',
          startPractice: 'Start practice',
          endSession: 'End session',
          loadingScenario: 'Loading scenario...',
          endingMessages: {
            analyzingPerformance: 'Analyzing your performance...',
            reviewActions: 'Reviewing your actions and providing insights...',
            generatingFeedback: 'Generating feedback and recommendations...',
            finalizingAnalysis:
              'Finalizing the analysis and preparing feedback...',
          },
          feedbackOptions: {
            practice_again: {
              label: '🔄 Practice Again',
              description: 'Start another role-play session',
            },
            discuss_feedback: {
              label: '💬 Discuss Feedback',
              description: 'Get more details about specific feedback points',
            },
          },
          feedback: {
            title: 'Rate Your Experience',
            subtitle:
              'How helpful was the roleplay in improving your confidence?',
            additionalFeedback: 'Additional Feedback',
            feedbackPlaceholder: 'Your comments here...',
            submit: 'Submit',
            close: 'Close',
            overview:
              "Let's review your role-play performance.\n\n{{scenarioOverview}}",
            strengths_header: 'Key Strengths',
            improvements_header: 'Areas for Improvement',
            next_steps_header: 'Suggested Next Steps',
            final_prompt:
              'Would you like to practice again or discuss any specific aspect of the feedback?',
          },
        },
        roleplay: {
          preparation: 'Preparing your personalized practice session...',
          connecting: 'Connecting to AI Coach',
          starting: 'Starting practice session...',
          ending: 'Generating Feedback',
          scenarios: {
            title: 'Scenario:',
            goal: 'Goal:',
            objective: 'Objective',
          },
          controls: {
            skipIntro: 'Skip intro',
            endPractice: 'End practice',
            restart: 'Restart',
          },
        },
        modules: {
          hiring: {
            title: 'Prepare for Hiring Interviews',
            description:
              'Master interviewing techniques and candidate evaluation',
            initialMessage: `Hey {{userName}}, I’m thrilled to be working with you today on mastering the art of interviewing! Whether you’re new to conducting interviews or just looking to fine-tune your skills, I’m here to help. We’ll dive into proven techniques for assessing candidates effectively, help you stay consistent with structured interviews, and give you a chance to practice those crucial conversations.
            Interviewing is a skill that can make or break a team, and with the right tools, you’ll be able to make informed, fair decisions. How are you feeling about your next round of interviews? Are there specific areas you’d like to focus on?`,
            submodules: {
              practice_interview: {
                title: 'I want to practice conducting hiring interviews',
                description: 'Practice structured interviewing techniques',
              },
              behavioral_interview: {
                title: 'I want to practice behavioral interviewing',
                description:
                  'Learn to assess past experiences and behaviors to predict future performance',
              },
              technical_interview: {
                title: 'I want to practice technical interviews',
                description:
                  'Develop skills in evaluating technical competencies and problem-solving abilities',
              },
              culture_fit: {
                title: 'I want to assess cultural fit',
                description:
                  'Practice evaluating candidates for organizational culture alignment',
              },
            },
          },
          onboarding: {
            title: 'Onboard New Hires',
            description: 'Set expectations and align new team members',
            icon: '🚀',
            initialMessage: `Hi {{userName}}, welcome to your onboarding coaching session! Onboarding is the first impression a new hire gets of your team, and it sets the stage for their success. Today, we'll work on aligning expectations, setting clear goals, and helping you build a positive, effective onboarding process.
            As a leader, it’s crucial to make your new hires feel supported and set them up for success from day one. How do you feel about your current onboarding process? Are there any particular challenges or areas you'd like to improve?`,
            submodules: {
              practice_expectations: {
                title:
                  'I want to practice creating alignment around performance expectations with a new team member',
                description: 'Set clear expectations and goals',
              },
              team_integration: {
                title:
                  'I want to practice introducing a new hire to the team and establishing team dynamics',
                description:
                  'Foster positive team relationships and collaboration from day one',
              },
              feedback_mechanisms: {
                title:
                  'I want to practice establishing feedback channels with a new hire',
                description: 'Create open communication and feedback loops',
              },
            },
          },
          career: {
            title: 'Lead Career Conversations',
            description: 'Guide career development discussions',
            initialMessage: `Hey {{userName}}, excited to help you lead impactful career conversations with your team members today! Career development is essential for keeping your high performers engaged, motivated, and growing in their roles. I’ll walk you through strategies to guide those crucial 1:1 discussions, offer actionable feedback, and ensure you’re fostering a culture of growth.
            These conversations can be the turning point for your team members’ career journeys. What’s on your mind heading into your next career conversation? Any specific goals or concerns?`,
            submodules: {
              practice_hipo_discuss: {
                title:
                  'I want help preparing for a 1:1 with a High Potential team member',
                description: 'Guide career development for high performers',
              },
              promo_conversations: {
                title:
                  'I want to practice discussing promotion readiness of a team member initiated by them',
                description: 'Navigating Promotion Expectations',
              },
              growth_plans_discussion: {
                title: 'I want help team member create clear development plan',
                description:
                  'Co-create actionable growth plan aligned with career goals',
              },
            },
          },
          personalCareer: {
            title: 'Accelerate Your Career',
            description: 'Navigate your professional growth and development',
            icon: '⭐',
            initialMessage: `Hey {{userName}}, I'm excited to help you accelerate your career growth today! Whether you're looking to advance in your current role or explore new opportunities, we'll work on strategies to help you reach your professional goals.

  Career development is a journey of continuous growth and self-discovery. What aspects of your career would you like to focus on today?`,
            submodules: {
              strategic_planning: {
                title: 'I want to create a strategic career plan',
                description: 'Develop a roadmap for your career advancement',
              },
              promotion_readiness: {
                title: 'I want to prepare for my next promotion',
                description:
                  'Build skills and visibility for career progression',
              },
              skill_development: {
                title: 'I want to identify key skills to develop',
                description: 'Assess and plan your professional development',
              },
            },
          },
          influence: {
            title: 'Build Influence & Impact',
            description: 'Enhance your communication and leadership presence',
            icon: '🎯',
            initialMessage: `Hi {{userName}}, today we'll focus on building your influence and impact through effective communication. Whether it's public speaking, negotiation, or motivating others, these skills are crucial for career success.

  Let's explore how you can enhance your presence and make a stronger impact. What specific communication challenges would you like to work on?`,
            submodules: {
              public_speaking: {
                title: 'I want to improve my public speaking skills',
                description: 'Master presenting to groups with confidence',
              },
              negotiation: {
                title: 'I want to enhance my negotiation abilities',
                description: 'Learn effective negotiation techniques',
              },
              stakeholder: {
                title: 'I want to better influence stakeholders',
                description: 'Build relationships and manage expectations',
              },
            },
          },
          executivePresence: {
            title: 'Develop Executive Presence',
            description: 'Build gravitas and strategic thinking abilities',
            initialMessage: `Hey {{userName}}, today we'll focus on developing your executive presence and strategic mindset. Whether you're aiming for senior leadership roles or want to be seen as a more strategic contributor, these skills are essential for long-term career success.

            Let's explore how you can enhance your executive presence and strategic thinking. What specific aspects would you like to work on?`,
            submodules: {
              strategic_thinking: {
                title: 'I want to strengthen my strategic thinking',
                description: 'Develop and communicate strategic perspectives',
              },
              executive_communication: {
                title:
                  'I want to communicate more effectively with senior leaders',
                description: 'Master executive-level communications',
              },
              business_acumen: {
                title: 'I want to enhance my business and financial acumen',
                description:
                  'Build stronger business and financial understanding',
              },
            },
          },
          networking: {
            title: 'Expand Your Network',
            description: 'Build meaningful professional relationships',
            icon: '🤝',
            initialMessage: `Hey {{userName}}, I'm here to help you build and nurture your professional network effectively. Strong networks open doors to opportunities and accelerate career growth.

  Let's explore strategies for authentic networking and relationship building. What networking challenges would you like to focus on?`,
            submodules: {
              relationship_building: {
                title: 'I want to build authentic professional relationships',
                description: 'Create lasting professional connections',
              },
              visibility: {
                title: 'I want to increase my visibility in the organization',
                description: 'Enhance your professional presence and brand',
              },
              mentorship: {
                title: 'I want to find and engage with mentors',
                description: 'Develop meaningful mentoring relationships',
              },
            },
          },
          performance: {
            title: 'Optimise Team Performance',
            description: 'Drive team success through effective feedback',
            initialMessage: `Hey {{userName}}, today we’ll be focusing on optimizing team performance through effective feedback and coaching. Your ability to give constructive feedback and guide your team through growth moments is key to their success. Together, we’ll practice delivering feedback that is clear, supportive, and action-oriented.
            Strong performance is the result of continuous development, and your leadership can make all the difference. How do you feel about giving feedback to your team? Any specific performance challenges you'd like to tackle today?`,
            submodules: {
              practice_performance_feedback: {
                title:
                  'I want help discussing growth feedback and underperformance',
                description: 'Deliver constructive feedback effectively',
              },
            },
          },
          crisis: {
            title: 'Navigate Crisis & Employee Relations',
            description: 'Handle sensitive situations and conflicts',
            initialMessage: `Hi {{userName}}, today’s session is all about navigating sensitive situations and handling employee relations with care and confidence. Whether it’s resolving conflicts, addressing team tension, or managing a crisis, these conversations require emotional intelligence, empathy, and clear communication.
            As a leader, it's important to address these issues head-on with a calm and constructive approach. What’s been on your mind regarding team dynamics or any ongoing issues you'd like to work through?`,
            submodules: {
              practice_conflict_resolution: {
                title:
                  'I want to practice resolving conflict between team members',
                description: 'Mediate and resolve team conflicts',
              },
            },
          },
          wellbeing: {
            title: 'Manage Team Workload & Wellbeing',
            description: 'Support team health while maintaining performance',
            initialMessage: `Hey {{userName}}, today we’re focusing on managing team workload and wellbeing. Balancing performance and mental health is crucial for team success, and your role as a leader is to create a supportive environment that fosters growth and resilience. We’ll explore strategies for checking in with your team, setting boundaries, and supporting mental health.`,
            submodules: {
              practice_mental_health: {
                title:
                  'I want tips for guiding mental-health related conversations',
                description: 'Handle wellbeing discussions with care',
              },
            },
          },
        },
        namedialog: {
          title: `What's your name?`,
          placeholder: 'Enter your name',
          ctalabel: 'Start Coaching Session',
        },
        roleplay_cta: {
          practice: 'Practice',
          learn: 'Learn',
          generating:
            'Generating role-play scenario based on our conversation ...',
        },
        scenario_generation_messages: {
          message1:
            'Generating a personalized practice scenario based on our conversation...',
          message2: "We're analyzing your preferences...",
          message3: 'Almost there, fine-tuning everything...',
          message4:
            'Finalizing your scenario with the best possible details...',
          message5: 'Just a few more moments, putting everything in place...',
        },
        network: {
          connection_quality: 'Connection Quality',
          latency: 'Latency',
        },
        avatar: {
          speaking: 'Speaking',
          listening: 'Listening',
        },
        transcript: {
          ai_coach: 'AI Coach',
          you: 'You',
        },
        error: {
          title: 'Connection Error',
          message:
            'Unable to connect to the AI Coach. Please check your internet connection and try again.',
          try_again: 'Try Again',
        },
        metrics: {
          title: 'Session Metrics',
          speaking_time: 'Speaking Time',
          response_speed: 'Response Speed',
          turn_taking: 'Turn-taking',
        },
        loading: {
          title: {
            connecting: 'Connecting to AI Coach',
            ending: 'Generating Feedback',
            loading: 'Loading Scenario',
            error: 'Error',
          },
          message: {
            connecting: 'Establishing secure connection...',
            ending: 'Analyzing your performance...',
            loading: 'Preparing your personalized practice session...',
            error:
              'Unable to connect to the AI Coach. Please check your internet connection and try again.',
          },
        },
        scenario: {
          phase: {
            pre_start: 'Ready to Start',
            intro: 'Introduction',
            main: 'Main Conversation',
          },
          details: {
            objective: 'Objective',
            framework: 'Framework',
            no_framework: 'No Framework',
            view_details: 'View Framework Details',
            title: 'Scenario Details',
            summary: 'Summary',
          },
          buttons: {
            start: 'Start Practice',
            end: 'End Session',
          },
        },
        framework_modal: {
          close: 'Close',
        },
        roleplay_area: {
          scenario_section: {
            title: 'Scenario',
          },
          ai_coach: {
            alt: 'AI Coach',
          },
        },
        control_panel: {
          skip_intro: 'Skip Introduction',
          end_session: 'End Session',
          ai_status: {
            speaking: 'AI Speaking',
            listening: 'AI Listening',
          },
        },
        connection_monitor: {
          messages: {
            offline: 'No internet connection',
            good: 'Strong connection',
            fair: 'Fair connection - may experience delays',
            poor: 'Poor connection - may experience delays',
            checking: 'Checking connection...',
          },
        },
      },
    },
  },
  ko: {
    translation: {
      coach: {
        landing: {
          title: '당신의 개인 리더십 코치',
          title1: '당신의 개인 리더십 코치',
          title2: '',
          subtitle: '슬랙에서',
          description:
            '맞춤형 코칭과 즉각적인 피드백 시스템을 통해 더 훌륭한 리더로 성장하세요.',
          startTrial: '무료 체험 시작하기',
          requestAccess: {
            title: '액세스 요청',
            subtitle: 'AI 리더십 코치를 시작하려면 이메일을 입력하세요',
            button: '액세스 요청',
            inputPlaceholder: '이메일 입력',
          },
          features: {
            personalCoach: {
              title: '나만의 AI 코치',
              description:
                '개인별 과제와 목표를 이해하는 AI로부터 맞춤형 코칭을 받으세요.',
            },
            realScenarios: {
              title: '시나리오 기반 연습',
              description:
                '실제 상황을 바탕으로 한 시나리오를 통해 연습하며 필요한 역랑을 키워보세요.',
            },
            immediateFeedback: {
              title: '즉각적인 피드백',
              description:
                '세션이 끝난 후, 리더십 스킬 향상을 위한 즉각적이고 실행 가능한 피드백을 받으세요.',
            },
          },
        },
        categories: {
          title: '무엇이 가장 고민이신가요?',
          subtitle: '팀을 효과적으로 관리하기 위한 맞춤형 코칭을 경험하세요.',
          nameDialog: {
            title: '시작하겠습니다',
            inputPlaceholder: '이름을 입력하세요',
            button: '계속하기',
          },
        },
        chat: {
          sidebar: {
            home: '홈',
          },
          typing: 'AI 코치가 입력 중...',
          inputPlaceholder: '메시지를 입력하세요...',
          establishConnection: '보안 연결 설정 중...',
          prepareSession: '맞춤형 연습 세션을 준비하고 있습니다...',
          sendButton: '보내기',
          startPractice: '연습 시작',
          endSession: '세션 종료',
          loadingScenario: '시나리오 로딩 중...',
          endingMessages: {
            analyzingPerformance: '수행 분석 중...',
            reviewActions: '행동을 검토하고 인사이트를 제공하는 중...',
            generatingFeedback: '피드백과 추천사항 ���성 중...',
            finalizingAnalysis: '분석을 마무리하고 피드백을 준비하는 중...',
          },
          feedbackOptions: {
            practice_again: {
              label: '🔄 다시 연습하기',
              description: '새로운 롤플레이 세션 시작',
            },
            discuss_feedback: {
              label: '💬 피드백 논의하기',
              description: '특정 피드백 포인트에 대해 자세히 알아보기',
            },
          },
          feedback: {
            title: '경험을 평가해주세요',
            subtitle: '롤플레이가 자신감 향상에 얼마나 도움이 되었나요?',
            additionalFeedback: '추가 피드백',
            feedbackPlaceholder: '의견을 입력해주세요...',
            submit: '제출',
            close: '닫기',
            overview:
              '롤플레이 수행에 대해 검토해보겠습니다.\n\n{{scenarioOverview}}',
            strengths_header: '주요 강점',
            improvements_header: '개선이 필요한 영역',
            next_steps_header: '제안하는 다음 단계',
            final_prompt:
              '다시 연습하시거나 피드백의 특정 부분에 대해 논의하시겠습니까?',
          },
        },
        roleplay: {
          preparation: '맞춤형 연습 세션을 준비하고 있습니다...',
          connecting: 'AI 코치에 연결 중',
          starting: '연습 세션 시작 중...',
          ending: '피드백 생성 중',
          scenarios: {
            title: '시나리오:',
            goal: '목표:',
            objective: '목표',
          },
          controls: {
            skipIntro: '소개 건너뛰기',
            endPractice: '연습 종료',
            restart: '다시 시작',
          },
        },
        modules: {
          hiring: {
            title: '채용 면접 준비',
            description: '면접 기술 및 지원자 평가 마스터하기',
            initialMessage: `안녕하세요 {{userName}}님, 오늘 면접 기술 마스터를 위해 함께 하게 되어 기쁩니다! 처음 면접을 진행하시든, 기존 기술을 향상시키고 싶으시든 도와드리겠습니다. 후보자를 효과적으로 평가하는 검증된 기술을 살펴보고, 구조화된 면접을 일관되게 진행하며, 중요한 대화를 연습할 기회를 제공해드립니다.
            면접은 팀의 성패를 좌우할 수 있는 기술이며, 적절한 도구를 갖추면 정보에 입각한 공정한 결정을 내릴 수 있습니다. 다음 면접에 대해 어떻게 느끼시나요? 특별히 집��하고 싶은 영역이 있으신가요?`,
            submodules: {
              practice_interview: {
                title: '채용 면접 진행 연습을 하고 싶습니다',
                description: '구조화된 면접 기술 연습',
              },
              behavioral_interview: {
                title: '행동 면접을 연습하고 싶습니다',
                description:
                  '과거 경험과 행동을 통해 미래 성과를 예측하는 방법 학습',
              },
              technical_interview: {
                title: '기술 면접을 연습하고 싶습니다',
                description: '기술적 역량과 문제 해결 능력 평가 기술 개발',
              },
              culture_fit: {
                title: '문화적 적합성을 평가하고 싶습니다',
                description: '조직 문화 적합성 평가 연습',
              },
            },
          },
          onboarding: {
            title: '신입 사원 온보딩',
            description: '기대치 설정 및 새 팀원 적응 돕기',
            icon: '🚀',
            initialMessage: `안녕하세요 {{userName}}님, 온보딩 코칭 세션에 오신 것을 환영합니다! 온보딩은 신입 사원이 팀에 대해 받는 첫인상이며, 그들의 성공을 위한 기반을 마련합니다. 오늘은 기대치 조율, 명확한 목표 설정, 긍정적이고 효과적인 온보딩 프로세스 구축을 함께 해보겠습니다.
            리더로서 신입 사원들이 첫날부터 지원받고 있다고 느끼게 하고 성공을 위한 준비를 하는 것이 중요합니다. 현재 온보딩 프로세스에 대해 어떻게 생각하시나요? 개선하고 싶은 특별한 과제나 영역이 있으신가요?`,
            submodules: {
              practice_expectations: {
                title:
                  '새로운 팀원과 성과 기대치에 대한 조율을 연습하고 싶습니다',
                description: '명확한 기대치와 목표 설정',
              },
              team_integration: {
                title:
                  '신입 사원을 팀에 소개하고 팀 역학을 확립하는 연습을 하고 싶습니다',
                description: '첫날부터 긍정적인 팀 관계와 협업 조성',
              },
              feedback_mechanisms: {
                title:
                  '신입 사원과 피드백 채널을 구축하는 연습을 하고 싶습니다',
                description: '열린 소통과 피드백 루프 생성',
              },
            },
          },
          career: {
            title: '커리어 면담 진행하기',
            description: '핵심 인재와 맞춤형 성장 계획 수립하기',
            initialMessage: `안녕하세요 {{userName}}님, 오늘 팀원들과의 영향력 있는 경력 대화를 이끄는 것을 도와드리게 되어 기쁩니다! 경력 개발은 고성과자들의 참여도를 유지하고, 동기부여하며, 역할 내에서 성장하도록 하는 데 필수입니다. 중요한 1:1 논의 이끄는 전략을 안내하고, 실행 가능한 피드백을 제공하며, 성장 문화를 조성하는 방법을 알려드리겠습니다.
            이러한 대화는 팀원들의 경력 여정에서 전환점이 될 수 있습니다. 다음 경력 대화를 앞두고 어떤 생각이 드시나요? 특별한 목표나 고민이 있으신가요?`,
            submodules: {
              practice_hipo_discuss: {
                title: '잠재력 높은 팀원과의 1:1 준비를 도움받고 싶습니다',
                description: '고성과자를 위한 경력 개발 안내',
              },
              promo_conversations: {
                title:
                  '팀원이 제기한 승진 준비도에 대해 논의하는 연습을 하고 싶습니다',
                description: '승진 기대치 관리하기',
              },
              growth_plans_discussion: {
                title: '팀원의 명확한 개발 계획 수립을 도와주고 싶습니다',
                description: '경력 목표에 맞는 실행 가능한 성장 계획 공동 수립',
              },
            },
          },
          personalCareer: {
            title: '경력 성장 가속화',
            description: '전문적 성장과 발전을 위한 체계적인 접근',
            initialMessage: `안녕하세요 {{userName}}님, 오늘은 귀하의 경력 성장을 가속화하는 데 집중하겠습니다! 현재 역할에서의 발전이든 새로운 기회를 탐색하든, 귀하의 전문적 목표 달성을 위한 전략을 함께 만들어보겠습니다.

            경력 개발은 지속적인 성장과 자기 발견의 여정입니다. 오늘은 경력의 어떤 측면에 초점을 맞추고 싶으신가요?`,
            submodules: {
              strategic_planning: {
                title: '전략적 경력 계획을 수립하고 싶습니다',
                description: '경력 발전을 위한 로드맵 개발',
              },
              promotion_readiness: {
                title: '다음 승진을 준비하고 싶습니다',
                description: '경력 발전을 위한 역량 개발과 가시성 확보',
              },
              skill_development: {
                title: '개발이 필요한 핵심 역량을 파악하고 싶습니다',
                description: '전문성 개발 계획 수립 및 평가',
              },
            },
          },

          influence: {
            title: '영향력과 임팩트 구축',
            description: '커뮤니케이션과 리더십 프레젠스 향상',
            initialMessage: `안녕하세요 {{userName}}님, 오늘은 효과적인 커뮤니케이션을 통해 영향력과 임팩트를 구축하는 데 집중하겠습니다. 대중 연설, 협상, 또는 다른 사람들에게 동기를 부여하는 것 등은 경력 성공을 위한 핵심 역량입니다.

            귀하의 프레젠스를 강화하고 더 강력한 임팩트를 만드는 방법을 탐색해보겠습니다. 어떤 커뮤니케이션 과제에 초점을 맞추고 싶으신가요?`,
            submodules: {
              public_speaking: {
                title: '대중 연설 기술을 향상시키고 싶습니다',
                description: '자신감 있는 그룹 프레젠테이션 마스터하기',
              },
              negotiation: {
                title: '협상 능력을 강화하고 싶습니다',
                description: '효과적인 협상 기법 학습',
              },
              stakeholder: {
                title: '이해관계자들에 대한 영향력을 높이고 싶습니다',
                description: '관계 구축 및 기대치 관리',
              },
            },
          },
          executivePresence: {
            title: '임원급 프레젠스 개발',
            description: '무게감과 전략적 사고 능력 구축',
            initialMessage: `안녕하세요 {{userName}}님, 오늘은 임원급 프레젠스와 전략적 사고방식을 개발하는 데 집중하겠습니다. 고위 리더십 역할을 목표로 하시든 더 전략적인 기여자로 인정받고 싶으시든, 이러한 스킬은 장기적인 경력 성공에 필수적입니다.

            임원급 프레젠스와 전략적 사고를 어떻게 강화할 수 있을지 탐색해보겠습니다. 어떤 특정 측면에 집중하고 싶으신가요?`,
            submodules: {
              strategic_thinking: {
                title: '전략적 사고를 강화하고 싶습니다',
                description: '전략적 관점 개발 및 커뮤니케이션',
              },
              executive_communication: {
                title: '고위 경영진과 더 효과적으로 소통하고 싶습니다',
                description: '임원급 커뮤니케이션 마스터하기',
              },
              business_acumen: {
                title: '비즈니스와 재무 감각을 향상시키고 싶습니다',
                description: '비즈니스와 재무에 대한 이해도 강화',
              },
            },
          },
          performance: {
            title: '팀 성과 최적화',
            description: '효과적인 피드백을 통해 팀 성공 촉진시키기',
            initialMessage: `안녕하세요 {{userName}}님, 오늘은 효과적��� 피드백과 코칭을 통한 팀 성과 최적화에 중점을 두겠습니다. 건설적인 피드백을 제공하고 팀의 성장 순간을 이끄는 능력은 그들의 성공에 핵심입니다. 함께 명확하고, 지원이며, 행동 지향적인 피드백을 전달하는 연습을 하겠습니다.
            강력한 성과는 지속적인 발전의 결과이며, 귀하의 리더십이 큰 차이를 만들 수 있습니다. 팀에게 피드백을 주는 것에 대해 어떻게 느끼시나요? 오늘 다루고 싶은 특별한 성과 과제가 있으신가요?`,
            submodules: {
              practice_performance_feedback: {
                title:
                  '성장 피드백과 저성과에 대해 논의하는 것을 도움받고 싶습니다',
                description: '건설적인 피드백 효과적으로 전달하기',
              },
            },
          },
          crisis: {
            title: '직장 내 갈등 관리',
            description: '민감한 상황에서의 갈등 해결하기',
            initialMessage: `안녕하세요 {{userName}}님, 오늘 세션은 민감한 상황을 다루고 직원 관계를 신중하고 자신감 있게 관리하는 것에 관한 것입니다. 갈등 해결, 팀 긴장 해소, 위기 관리 등의 대화에는 감성 지능, 공감, 명확한 소통이 필��합니다.
            리더로서 이러한 문제들을 차분하고 건설적인 접근으로 정면으로 다루는 것이 중요합니다. 팀 역학이나 현재 진행 중인 문제 중 함께 해결하고 싶은 것이 있으신가요?`,
            submodules: {
              practice_conflict_resolution: {
                title: '팀원 간 갈등 해결을 연습하고 싶습니다',
                description: '팀 갈등 중재 및 해결',
              },
            },
          },
          wellbeing: {
            title: '팀 업무량 및 웰빙 관리',
            description: '성과를 유지하면서 팀 업무강도 관리하기',
            initialMessage: `안녕하세요 {{userName}}님, 오늘은 팀 업무량과 웰빙 관리에 중점을 두겠습니다. 성과와 정신 건강의 균형을 맞추는 것은 팀 성공에 매우 중요하며, 리더로서 귀하의 역할은 성장과 회복력을 촉진하는 지원적인 환경을 조성하는 것입니다. 팀과의 소통, 경계 설정, 정신 건강 지원을 위한 전략을 살펴보겠습니다.`,
            submodules: {
              practice_mental_health: {
                title: '정신 건강 관련 대화 안내를 위한 팁이 필요합니다',
                description: '웰빙 논의를 신중하게 다루기',
              },
            },
          },
        },
        namedialog: {
          title: '이름이 무엇인가요?',
          placeholder: '이름을 입력하세요',
          ctalabel: '코칭 세션 시작하기',
        },
        roleplay_cta: {
          practice: '연습하기',
          learn: '학습하기',
          generating: '대화를 기반으로 연습 모듈 선택 중...',
        },
        scenario_generation_messages: {
          message1: '대화를 기반으로 맞춤형 연습 시나리오 생성 중...',
          message2: '귀하의 선호도를 분석 중...',
          message3: '거의 왔어요, 모든 것을 세밀하게 조정 중...',
          message4: '최상의 세부 정보로 시나리오를 마무리 중...',
          message5: '조금만 더 기다려주세요, 모든 것을 준비 중...',
        },
        network: {
          connection_quality: '연결 품질',
          latency: '지연 시간',
        },
        avatar: {
          speaking: '말하는 중',
          listening: '듣는 중',
        },
        transcript: {
          ai_coach: 'AI 코치',
          you: '나',
        },
        error: {
          title: '연결 오류',
          message:
            'AI 코치에 연결할 수 없습니다. 인터넷 연결을 확인하고 다시 시도해주세요.',
          try_again: '다시 시도',
        },
        metrics: {
          title: '세션 지표',
          speaking_time: '말하기 시간',
          response_speed: '응답 속도',
          turn_taking: '대화 주고받기',
        },
        loading: {
          title: {
            connecting: 'AI 코치에 연결 중',
            ending: '피드백 생성 중',
            loading: '시나리오 로딩 중',
            error: '오류 발생',
          },
          message: {
            connecting: '보안 연결 설정 중...',
            ending: '수행 분석 중...',
            loading: '맞춤형 연습 세션 준비 중...',
            error: '오류 발생',
          },
        },
        scenario: {
          phase: {
            pre_start: '시작 준비',
            intro: '소개',
            main: '주요 대화',
          },
          details: {
            objective: '목표',
            framework: '프레임워크',
            no_framework: '프레임워크 없음',
            view_details: '프레임워크 세부사항 보기',
            title: '시나리오 세부사항',
            summary: '요약',
          },
          buttons: {
            start: '연습 시작',
            end: '세션 종료',
          },
        },
        framework_modal: {
          close: '닫기',
        },
        roleplay_area: {
          scenario_section: {
            title: '시나리오',
          },
          ai_coach: {
            alt: 'AI 코치',
          },
        },
        control_panel: {
          skip_intro: '소개 건너뛰기',
          end_session: '세션 종료',
          ai_status: {
            speaking: 'AI 말하는 중',
            listening: 'AI 듣는 중',
          },
        },
        connection_monitor: {
          messages: {
            offline: '인터넷 연결 없음',
            good: '강한 연결',
            fair: '보통 연결 - 지연이 발생할 수 있음',
            poor: '약한 연결 - 지연이 발생할 수 있음',
            checking: '연결 확인 중...',
          },
        },
      },
    },
  },
  fr: {
    translation: {
      coach: {
        landing: {
          title: 'Votre coach en leadership personnalisé',
          title1: 'Votre',
          title2: 'coach en leadership',
          subtitle: 'dans votre Slack',
          description:
            'Bénéficiez d’un coaching personnalisé, entraînez-vous à mener des conversations difficiles et recevez des retours immédiats pour devenir un meilleur leader.',
          startTrial: "Commencer l'essai gratuit",
          requestAccess: {
            title: "Demander l'accès",
            subtitle:
              'Entrez votre email pour commencer avec notre coach en leadership IA',
            button: "Demander l'accès",
            inputPlaceholder: 'Entrez votre email',
          },
          features: {
            personalCoach: {
              title: 'Coach IA personnel',
              description:
                'Recevez un coaching personnalisé d’une IA qui comprend vos défis et objectifs spécifiques.',
            },
            realScenarios: {
              title: 'Scénarios réels',
              description:
                'Entraînez-vous avec des scénarios réalistes adaptés à votre niveau d’expérience et à votre situation actuelle.',
            },
            immediateFeedback: {
              title: 'Retour immédiat',
              description:
                'Recevez des retours immédiats et exploitables pour améliorer vos compétences en leadership après chaque session.',
            },
          },
        },
        categories: {
          title: "Qu'est-ce qui vous tient le plus à cœur ?",
          subtitle:
            'Accédez à un coaching personnalisé et à des conseils pour gérer votre équipe',
          nameDialog: {
            title: "C'est parti !",
            inputPlaceholder: 'Entrez votre nom',
            button: 'Continuer',
          },
        },
        chat: {
          sidebar: {
            home: 'Accueil',
          },
          typing: 'Le coach IA est en train de taper...',
          inputPlaceholder: 'Tapez votre message...',
          establishConnection: 'Établissement d’une connexion sécurisée...',
          prepareSession:
            'Préparation de votre session d’entraînement personnalisée...',
          sendButton: 'Envoyer',
          startPractice: 'Commencer l’entraînement',
          endSession: 'Terminer la session',
          loadingScenario: 'Chargement du scénario...',
          endingMessages: {
            analyzingPerformance: 'Analyse de votre performance...',
            reviewActions: 'Examen de vos actions et fourniture d’analyses...',
            generatingFeedback:
              'Génération de retours et de recommandations...',
            finalizingAnalysis:
              'Finalisation de l’analyse et préparation des retours...',
          },
          feedbackOptions: {
            practice_again: {
              label: '🔄 Réessayer',
              description: 'Lancer une nouvelle session de jeu de rôle',
            },
            discuss_feedback: {
              label: '💬 Discuter des retours',
              description:
                'Obtenez plus de détails sur des points de retours spécifiques',
            },
          },
          feedback: {
            title: 'Évaluez votre expérience',
            subtitle:
              'Quelle a été l’utilité de ce jeu de rôle pour renforcer votre confiance ?',
            additionalFeedback: 'Retours supplémentaires',
            feedbackPlaceholder: 'Vos commentaires ici...',
            submit: 'Soumettre',
            close: 'Fermer',
            overview:
              'Examinons votre performance en jeu de rôle.\n\n{{scenarioOverview}}',
            strengths_header: 'Forces clés',
            improvements_header: 'Points à améliorer',
            next_steps_header: 'Prochaines étapes suggérées',
            final_prompt:
              'Souhaitez-vous réessayer ou discuter d’un aspect particulier des retours ?',
          },
        },
        roleplay: {
          preparation:
            'Préparation de votre session d’entraînement personnalisée...',
          connecting: 'Connexion au coach IA',
          starting: 'Démarrage de la session d’entraînement...',
          ending: 'Génération des retours',
          scenarios: {
            title: 'Scénario :',
            goal: 'But :',
            objective: 'Objectif',
          },
          controls: {
            skipIntro: "Passer l'introduction",
            endPractice: 'Terminer l’entraînement',
            restart: 'Redémarrer',
          },
        },
        modules: {
          hiring: {
            title: 'Préparez-vous aux entretiens de recrutement',
            description:
              "Maîtrisez les techniques d'entretien et l'évaluation des candidats",
            initialMessage:
              "Salut {{userName}}, je suis ravi de travailler avec vous aujourd'hui pour maîtriser l'art de l'entretien ! Que vous soyez novice ou que vous cherchiez à perfectionner vos compétences, je suis là pour vous aider. Nous explorerons des techniques éprouvées pour évaluer efficacement les candidats, assurer des entretiens structurés et pratiquer ces conversations cruciales.\nL'entretien est une compétence qui peut faire ou défaire une équipe. Avec les bons outils, vous prendrez des décisions éclairées et équitables. Que pensez-vous de votre prochaine série d'entretiens ? Y a-t-il des domaines particuliers sur lesquels vous souhaitez vous concentrer ?",
            submodules: {
              practice_interview: {
                title:
                  'Je souhaite m’exercer à conduire des entretiens de recrutement',
                description:
                  "Entraînez-vous aux techniques d'entretien structuré",
              },
              behavioral_interview: {
                title: 'Je souhaite m’exercer aux entretiens comportementaux',
                description:
                  'Apprenez à évaluer les expériences passées et les comportements pour prédire les performances futures',
              },
              technical_interview: {
                title: 'Je souhaite m’exercer aux entretiens techniques',
                description:
                  'Développez des compétences pour évaluer les aptitudes techniques et la capacité à résoudre des problèmes',
              },
              culture_fit: {
                title: "Je souhaite évaluer l'adéquation culturelle",
                description:
                  "Entraînez-vous à évaluer l'adéquation des candidats à la culture de l'entreprise",
              },
            },
          },
          onboarding: {
            title: 'Intégrez les nouveaux employés',
            description:
              'Définissez des attentes et alignez les nouveaux membres de l’équipe',
            icon: '🚀',
            initialMessage:
              "Bonjour {{userName}}, bienvenue à votre session de coaching sur l'intégration ! L'onboarding est la première impression qu’un nouvel employé a de votre équipe et pose les bases de sa réussite. Aujourd'hui, nous travaillerons à aligner les attentes, définir des objectifs clairs et bâtir un processus d'intégration positif et efficace.\nEn tant que leader, il est essentiel que vos nouveaux collaborateurs se sentent soutenus dès le premier jour. Que pensez-vous de votre processus d'intégration actuel ? Y a-t-il des défis ou des aspects à améliorer ?",
            submodules: {
              practice_expectations: {
                title:
                  "Je souhaite m'exercer à aligner les attentes de performance avec un nouveau membre de l'équipe",
                description: 'Définir des attentes et objectifs clairs',
              },
              team_integration: {
                title:
                  "Je souhaite m'exercer à présenter un nouveau collaborateur à l'équipe et instaurer une dynamique de groupe",
                description:
                  'Favoriser des relations d’équipe positives et une collaboration dès le premier jour',
              },
              feedback_mechanisms: {
                title:
                  "Je souhaite m'exercer à instaurer des canaux de feedback avec un nouveau collaborateur",
                description:
                  'Créer une communication ouverte et des boucles de feedback',
              },
            },
          },
          career: {
            title: 'Menez des entretiens de carrière',
            description:
              'Animez des discussions sur le développement de carrière',
            initialMessage:
              "Salut {{userName}}, je suis enthousiaste à l'idée de vous aider à mener des entretiens de carrière impactants avec vos collaborateurs aujourd'hui ! Le développement de carrière est essentiel pour maintenir l'engagement et la motivation de vos talents. Je vous présenterai des stratégies pour animer ces entretiens individuels, offrir des retours concrets et favoriser une culture de la croissance.\nCes conversations peuvent être déterminantes pour l'avenir professionnel de vos équipes. Quelles sont vos attentes pour votre prochain entretien de carrière ? Des objectifs ou préoccupations particuliers ?",
            submodules: {
              practice_hipo_discuss: {
                title:
                  "J'ai besoin d'aide pour préparer un entretien individuel avec un collaborateur à haut potentiel",
                description:
                  'Orienter le développement de carrière des talents performants',
              },
              promo_conversations: {
                title:
                  "Je souhaite m’exercer à discuter de la préparation à la promotion d'un collaborateur, à son initiative",
                description: 'Gérer les attentes liées à la promotion',
              },
              growth_plans_discussion: {
                title:
                  "J'ai besoin d'aide pour élaborer un plan de développement clair pour un collaborateur",
                description:
                  'Co-créer un plan de croissance concret aligné sur les objectifs de carrière',
              },
            },
          },
          personalCareer: {
            title: 'Accélérez votre carrière',
            description:
              'Pilotez votre évolution professionnelle et votre développement',
            icon: '⭐',
            initialMessage:
              "Salut {{userName}}, je suis ravi de vous aider à accélérer votre progression professionnelle aujourd'hui ! Que vous souhaitiez évoluer dans votre poste actuel ou explorer de nouvelles opportunités, nous travaillerons sur des stratégies pour atteindre vos objectifs professionnels.\n\nLe développement de carrière est un voyage de croissance continue et de découverte de soi. Sur quels aspects de votre carrière souhaitez-vous vous concentrer aujourd'hui ?",
            submodules: {
              strategic_planning: {
                title: 'Je souhaite élaborer un plan de carrière stratégique',
                description:
                  'Élaborer une feuille de route pour votre progression de carrière',
              },
              promotion_readiness: {
                title: 'Je veux me préparer pour ma prochaine promotion',
                description:
                  'Développer des compétences et gagner en visibilité pour progresser',
              },
              skill_development: {
                title:
                  'Je souhaite identifier les compétences clés à développer',
                description:
                  'Évaluer et planifier votre développement professionnel',
              },
            },
          },
          influence: {
            title: 'Renforcez votre influence et votre impact',
            description:
              'Améliorez votre communication et votre présence de leader',
            initialMessage:
              "Bonjour {{userName}}, aujourd'hui nous allons renforcer votre influence et votre impact grâce à une communication efficace. Que ce soit pour prendre la parole en public, négocier ou motiver, ces compétences sont essentielles pour réussir. Explorons comment améliorer votre présence et créer un impact plus fort. Quels défis en communication souhaitez-vous relever ?",
            submodules: {
              public_speaking: {
                title:
                  'Je souhaite améliorer mes compétences en prise de parole en public',
                description:
                  'Maîtriser l’art de présenter devant un groupe avec assurance',
              },
              negotiation: {
                title: 'Je veux améliorer mes compétences en négociation',
                description: 'Apprenez des techniques de négociation efficaces',
              },
              stakeholder: {
                title: 'Je souhaite mieux influencer les parties prenantes',
                description: 'Développer des relations et gérer les attentes',
              },
            },
          },
          executivePresence: {
            title: 'Développez votre présence exécutive',
            description:
              'Cultivez de la prestance et des capacités de réflexion stratégique',
            initialMessage:
              "Salut {{userName}}, aujourd'hui nous allons travailler sur votre présence exécutive et votre esprit stratégique. Que vous visiez des postes de direction ou souhaitiez être perçu comme un contributeur stratégique, ces compétences sont essentielles pour réussir à long terme. Explorons ensemble comment renforcer votre présence exécutive et votre réflexion stratégique. Quels aspects spécifiques souhaitez-vous travailler ?",
            submodules: {
              strategic_thinking: {
                title: 'Je souhaite renforcer ma pensée stratégique',
                description:
                  'Développer et communiquer des perspectives stratégiques',
              },
              executive_communication: {
                title:
                  'Je veux communiquer plus efficacement avec les cadres supérieurs',
                description: 'Maîtriser la communication au niveau exécutif',
              },
              business_acumen: {
                title:
                  'Je souhaite améliorer ma compréhension des affaires et de la finance',
                description:
                  'Renforcez votre compréhension des affaires et des finances',
              },
            },
          },
          networking: {
            title: 'Développez votre réseau',
            description:
              'Construisez des relations professionnelles significatives',
            icon: '🤝',
            initialMessage:
              'Salut {{userName}}, je suis là pour vous aider à développer et entretenir efficacement votre réseau professionnel. Un réseau solide ouvre des portes et accélère la croissance de carrière. Explorons ensemble des stratégies pour un networking authentique et la construction de relations. Quels défis en réseautage souhaitez-vous relever ?',
            submodules: {
              relationship_building: {
                title:
                  'Je souhaite établir des relations professionnelles authentiques',
                description: 'Créer des connexions professionnelles durables',
              },
              visibility: {
                title:
                  "Je souhaite augmenter ma visibilité au sein de l'organisation",
                description:
                  'Renforcez votre présence professionnelle et votre image de marque',
              },
              mentorship: {
                title: 'Je veux trouver et interagir avec des mentors',
                description:
                  'Développer des relations de mentorat significatives',
              },
            },
          },
          performance: {
            title: 'Optimisez la performance de l’équipe',
            description:
              'Favorisez le succès de l’équipe grâce à des retours efficaces',
            initialMessage:
              "Salut {{userName}}, aujourd'hui nous allons nous concentrer sur l'optimisation de la performance de l’équipe grâce à des retours et un coaching efficaces. Votre capacité à fournir des feedbacks constructifs et à guider votre équipe dans ses moments de croissance est essentielle. Ensemble, nous pratiquerons des retours clairs, encourageants et orientés vers l'action. Comment ressentez-vous le fait de donner des retours à votre équipe ? Y a-t-il des défis spécifiques que vous souhaitez aborder ?",
            submodules: {
              practice_performance_feedback: {
                title:
                  "J'ai besoin d'aide pour discuter des retours sur la croissance et des performances insuffisantes",
                description:
                  'Fournir des retours constructifs de manière efficace',
              },
            },
          },
          crisis: {
            title: 'Gérez les crises et les relations avec les employés',
            description: 'Gérer des situations sensibles et les conflits',
            initialMessage:
              "Bonjour {{userName}}, la session d'aujourd'hui porte sur la gestion de situations sensibles et l'approche des relations avec les employés avec soin et assurance. Qu'il s'agisse de résoudre des conflits, d'atténuer les tensions au sein de l’équipe ou de gérer une crise, ces échanges nécessitent intelligence émotionnelle, empathie et communication claire. En tant que leader, il est important d'aborder ces problèmes de front avec calme et méthode. Qu'est-ce qui vous préoccupe au sujet de la dynamique de votre équipe ou d'éventuels problèmes en cours ?",
            submodules: {
              practice_conflict_resolution: {
                title:
                  'Je souhaite m’exercer à résoudre les conflits entre membres de l’équipe',
                description:
                  'Médier et résoudre les conflits au sein de l’équipe',
              },
            },
          },
          wellbeing: {
            title: 'Gérez la charge de travail et le bien-être de l’équipe',
            description:
              'Soutenez la santé de l’équipe tout en maintenant la performance',
            initialMessage:
              "Salut {{userName}}, aujourd'hui nous allons nous concentrer sur la gestion de la charge de travail et le bien-être de l’équipe. Trouver l'équilibre entre performance et santé mentale est crucial pour le succès de l’équipe, et votre rôle de leader consiste à créer un environnement propice à la croissance et à la résilience. Nous explorerons des stratégies pour suivre l’état de votre équipe, fixer des limites et soutenir la santé mentale.",
            submodules: {
              practice_mental_health: {
                title:
                  "J'ai besoin de conseils pour mener des discussions sur la santé mentale",
                description:
                  'Aborder les discussions sur le bien-être avec précaution',
              },
            },
          },
        },
        namedialog: {
          title: "What's your name?",
          placeholder: 'Enter your name',
          ctalabel: 'Start Coaching Session',
        },
        roleplay_cta: {
          practice: 'Pratiquer',
          learn: 'Apprendre',
          generating:
            "Génération d'un scénario de jeu de rôle basé sur notre conversation...",
        },
        scenario_generation_messages: {
          message1:
            "Génération d'un scénario d'entraînement personnalisé basé sur notre conversation...",
          message2: 'Nous analysons vos préférences...',
          message3: 'Presque terminé, ajustement final en cours...',
          message4:
            'Finalisation de votre scénario avec les meilleurs détails possibles...',
          message5: 'Encore quelques instants, nous mettons tout en place...',
        },
        network: {
          connection_quality: 'Qualité de la connexion',
          latency: 'Latence',
        },
        avatar: {
          speaking: 'Parle',
          listening: 'Écoute',
        },
        transcript: {
          ai_coach: 'Coach IA',
          you: 'Vous',
        },
        error: {
          title: 'Erreur de connexion',
          message:
            'Impossible de se connecter au coach IA. Veuillez vérifier votre connexion internet et réessayer.',
          try_again: 'Réessayer',
        },
        metrics: {
          title: 'Statistiques de session',
          speaking_time: 'Temps de parole',
          response_speed: 'Vitesse de réponse',
          turn_taking: 'Prise de parole',
        },
        loading: {
          title: {
            connecting: 'Connexion au coach IA',
            ending: 'Génération des retours',
            loading: 'Chargement du scénario',
            error: 'Erreur',
          },
          message: {
            connecting: 'Établissement d’une connexion sécurisée...',
            ending: 'Analyse de votre performance...',
            loading:
              'Préparation de votre session d’entraînement personnalisée...',
            error:
              'Impossible de se connecter au coach IA. Veuillez vérifier votre connexion internet et réessayer.',
          },
        },
        scenario: {
          phase: {
            pre_start: 'Prêt à démarrer',
            intro: 'Introduction',
            main: 'Conversation principale',
          },
          details: {
            objective: 'Objectif',
            framework: 'Cadre',
            no_framework: 'Pas de cadre',
            view_details: 'Voir les détails du cadre',
            title: 'Détails du scénario',
            summary: 'Résumé',
          },
          buttons: {
            start: 'Commencer l’entraînement',
            end: 'Terminer la session',
          },
        },
        framework_modal: {
          close: 'Fermer',
        },
        roleplay_area: {
          scenario_section: {
            title: 'Scénario',
          },
          ai_coach: {
            alt: 'Coach IA',
          },
        },
        control_panel: {
          skip_intro: "Passer l'introduction",
          end_session: 'Terminer la session',
          ai_status: {
            speaking: 'Coach IA parle',
            listening: 'Coach IA écoute',
          },
        },
        connection_monitor: {
          messages: {
            offline: 'Pas de connexion internet',
            good: 'Connexion forte',
            fair: 'Connexion moyenne - des retards peuvent survenir',
            poor: 'Connexion faible - des retards peuvent survenir',
            checking: 'Vérification de la connexion...',
          },
        },
      },
    },
  },
  ja: {
    translation: {
      coach: {
        landing: {
          title: 'あなた専用のリーダーシップコーチ',
          title1: 'あなたの',
          title2: 'リーダーシップコーチ',
          subtitle: 'Slack内で',
          description:
            'パーソナライズされたコーチングを受け、難しい会話を練習し、即時のフィードバックを受け取って、より優れたリーダーに成長しましょう。',
          startTrial: '無料トライアルを開始',
          requestAccess: {
            title: 'アクセスをリクエスト',
            subtitle:
              'AIリーダーシップコーチを始めるためにメールアドレスを入力してください',
            button: 'アクセスをリクエスト',
            inputPlaceholder: 'メールアドレスを入力',
          },
          features: {
            personalCoach: {
              title: 'パーソナルAIコーチ',
              description:
                'あなたの特定の課題と目標を理解するAIからパーソナライズされたコーチングを受けましょう。',
            },
            realScenarios: {
              title: '実際のシナリオ',
              description:
                'あなたの経験レベルと現在の状況に合わせたリアルなシナリオで練習しましょう。',
            },
            immediateFeedback: {
              title: '即時フィードバック',
              description:
                '各セッション後に即座に実践的なフィードバックを受け、リーダーシップスキルを向上させましょう。',
            },
          },
        },
        categories: {
          title: 'あなたの今、一番気になることは？',
          subtitle:
            'チーム管理のためのパーソナライズされたコーチングとガイダンスを提供します',
          nameDialog: {
            title: 'さあ、始めましょう',
            inputPlaceholder: '名前を入力してください',
            button: '続ける',
          },
        },
        chat: {
          sidebar: {
            home: 'ホーム',
          },
          typing: 'AIコーチが入力中…',
          inputPlaceholder: 'メッセージを入力...',
          establishConnection: '安全な接続を確立中…',
          prepareSession: 'あなた専用の練習セッションを準備中…',
          sendButton: '送信',
          startPractice: '練習を開始',
          endSession: 'セッション終了',
          loadingScenario: 'シナリオを読み込み中…',
          endingMessages: {
            analyzingPerformance: 'あなたのパフォーマンスを分析中…',
            reviewActions: 'あなたの行動をレビューし、洞察を提供中…',
            generatingFeedback: 'フィードバックと提案を生成中…',
            finalizingAnalysis: '分析を確定し、フィードバックを準備中…',
          },
          feedbackOptions: {
            practice_again: {
              label: '🔄 再練習',
              description: '別のロールプレイセッションを開始する',
            },
            discuss_feedback: {
              label: '💬 フィードバックを議論する',
              description: '特定のフィードバックポイントについて詳しく知る',
            },
          },
          feedback: {
            title: 'あなたの体験を評価してください',
            subtitle:
              'このロールプレイはあなたの自信向上にどれほど役立ちましたか？',
            additionalFeedback: '追加フィードバック',
            feedbackPlaceholder: 'コメントを入力...',
            submit: '送信',
            close: '閉じる',
            overview:
              'あなたのロールプレイのパフォーマンスを振り返りましょう。\n\n{{scenarioOverview}}',
            strengths_header: '主要な強み',
            improvements_header: '改善点',
            next_steps_header: '次のステップの提案',
            final_prompt:
              '再練習するか、フィードバックの特定の点について議論しますか？',
          },
        },
        roleplay: {
          preparation: 'あなた専用の練習セッションを準備中…',
          connecting: 'AIコーチに接続中',
          starting: '練習セッションを開始中…',
          ending: 'フィードバックを生成中',
          scenarios: {
            title: 'シナリオ：',
            goal: '目標：',
            objective: '目的',
          },
          controls: {
            skipIntro: 'イントロをスキップ',
            endPractice: '練習終了',
            restart: '再スタート',
          },
        },
        modules: {
          hiring: {
            title: '採用面接の準備をする',
            description: '面接技術と候補者評価をマスターする',
            initialMessage:
              'やあ、{{userName}}さん、今日あなたと一緒に面接の技術を極めることをとても楽しみにしています！面接を初めて行う方でも、スキルをさらに磨きたい方でも、私はあなたをサポートします。候補者を効果的に評価するための実証済みの技法に取り組み、構造化された面接を一貫して実施するための助けとなり、重要な会話の練習の機会を提供します。\n面接はチームの成功を左右するスキルであり、適切なツールがあれば、情報に基づいた公正な判断ができるようになります。次回の面接に向けてどのように感じていますか？特に注力したい分野はありますか？',
            submodules: {
              practice_interview: {
                title: '採用面接の実践をしたい',
                description: '構造化された面接技法の練習',
              },
              behavioral_interview: {
                title: '行動面接の実践をしたい',
                description:
                  '過去の経験や行動を評価し、将来のパフォーマンスを予測する方法を学ぶ',
              },
              technical_interview: {
                title: '技術面接の実践をしたい',
                description:
                  '技術的能力と問題解決能力を評価するスキルを身につける',
              },
              culture_fit: {
                title: 'カルチャーフィットの評価をしたい',
                description: '候補者の組織文化への適合性を評価する練習',
              },
            },
          },
          onboarding: {
            title: '新入社員のオンボーディング',
            description: '期待値を設定し、新しいチームメンバーを整合させる',
            icon: '🚀',
            initialMessage:
              'こんにちは、{{userName}}さん。オンボーディングコーチングセッションへようこそ！オンボーディングは新入社員がチームに対して受ける最初の印象であり、彼らの成功の基盤を築きます。本日は期待値の調整、明確な目標設定、そしてポジティブで効果的なオンボーディングプロセスの構築に取り組みます。\nリーダーとして、新入社員に初日からサポートを感じてもらうことが重要です。現在のオンボーディングプロセスについてどう感じていますか？改善したい特定の課題や分野はありますか？',
            submodules: {
              practice_expectations: {
                title: '新しいチームメンバーとの期待値の調整を練習したい',
                description: '明確な期待値と目標を設定する',
              },
              team_integration: {
                title:
                  '新入社員をチームに紹介し、チームダイナミクスを確立する練習をしたい',
                description: '初日からポジティブなチーム関係と協働を促進する',
              },
              feedback_mechanisms: {
                title: '新入社員とのフィードバックチャネルの確立を練習したい',
                description:
                  'オープンなコミュニケーションとフィードバックループを作る',
              },
            },
          },
          career: {
            title: 'キャリア面談の実施',
            description: 'キャリア開発のディスカッションを導く',
            initialMessage:
              'やあ、{{userName}}さん、今日あなたのチームメンバーとのインパクトのあるキャリア面談をリードするお手伝いができることを楽しみにしています！キャリア開発は、高いパフォーマンスを発揮する人材を引きつけ、モチベーションを維持し、成長を促すために不可欠です。1対1の面談をリードする戦略、実践的なフィードバックの提供、そして成長文化の醸成について一緒に取り組んでいきましょう。\nこれらの会話は、チームメンバーのキャリアの転機となり得ます。次回のキャリア面談に向けて、どんな気持ちですか？特に焦点を当てたい分野はありますか？',
            submodules: {
              practice_hipo_discuss: {
                title:
                  'ハイポテンシャルなチームメンバーとの1対1の準備を支援してほしい',
                description:
                  '高いパフォーマンスを発揮する人材のキャリア開発を導く',
              },
              promo_conversations: {
                title: 'チームメンバーからの昇進準備についての議論を練習したい',
                description: '昇進の期待値をうまく調整する',
              },
              growth_plans_discussion: {
                title: 'チームメンバーの明確な開発計画作成を支援してほしい',
                description:
                  'キャリア目標に沿った実行可能な成長計画を共同で作成する',
              },
            },
          },
          personalCareer: {
            title: 'キャリアを加速させる',
            description:
              'あなたのプロフェッショナルな成長と発展をナビゲートする',
            icon: '⭐',
            initialMessage:
              'やあ、{{userName}}さん、今日あなたのキャリア成長を加速させるためのお手伝いができることを嬉しく思います！現在の役割での昇進を目指す場合でも、新たな機会を探る場合でも、あなたのプロフェッショナルな目標達成に向けた戦略を一緒に考えていきましょう。\n\nキャリア開発は、継続的な成長と自己発見の旅です。今日はキャリアのどの側面に注力したいですか？',
            submodules: {
              strategic_planning: {
                title: '戦略的なキャリアプランを作成したい',
                description: 'キャリアの進展のためのロードマップを作成する',
              },
              promotion_readiness: {
                title: '次の昇進に備えたい',
                description: 'キャリア進展のためのスキルと可視性を向上させる',
              },
              skill_development: {
                title: '開発すべき主要なスキルを特定したい',
                description:
                  'あなたのプロフェッショナルな成長を評価し、計画を立てる',
              },
            },
          },
          influence: {
            title: '影響力とインパクトを構築する',
            description:
              'あなたのコミュニケーションとリーダーシップの存在感を高める',
            icon: '🎯',
            initialMessage:
              'こんにちは、{{userName}}さん、今日は効果的なコミュニケーションを通じてあなたの影響力とインパクトを高める方法に取り組みます。パブリックスピーキング、交渉、または他者を動機付けることなど、これらはキャリア成功のために不可欠なスキルです。\n\nあなたの存在感を強化し、より強いインパクトを生み出す方法を一緒に探りましょう。どのコミュニケーションの課題に取り組みたいですか？',
            submodules: {
              public_speaking: {
                title: 'パブリックスピーキングスキルを向上させたい',
                description: '自信を持ってグループの前で発表できるようになる',
              },
              negotiation: {
                title: '交渉能力を高めたい',
                description: '効果的な交渉技法を学ぶ',
              },
              stakeholder: {
                title: 'ステークホルダーへの影響力を高めたい',
                description: '関係を構築し、期待値を管理する',
              },
            },
          },
          executivePresence: {
            title: 'エグゼクティブ・プレゼンスを高める',
            description: '重厚感と戦略的思考能力を養う',
            initialMessage:
              'やあ、{{userName}}さん、今日はエグゼクティブ・プレゼンスと戦略的なマインドセットを磨くことに焦点を当てます。シニアリーダーシップのポジションを目指す場合でも、より戦略的な貢献者として認識されたい場合でも、これらのスキルは長期的なキャリア成功に不可欠です。\n\nあなたのエグゼクティブ・プレゼンスと戦略的思考をどのように向上させるか、一緒に探っていきましょう。どの具体的な側面に取り組みたいですか？',
            submodules: {
              strategic_thinking: {
                title: '戦略的思考を強化したい',
                description: '戦略的な視点を構築し、伝える能力を高める',
              },
              executive_communication: {
                title: 'シニアリーダーと効果的にコミュニケーションを取りたい',
                description:
                  'エグゼクティブレベルのコミュニケーションをマスターする',
              },
              business_acumen: {
                title: 'ビジネスとファイナンスの知識を向上させたい',
                description: 'ビジネスと財務の理解を深める',
              },
            },
          },
          networking: {
            title: 'ネットワーク拡大',
            description: '意味のあるプロフェッショナルな関係を築く',
            icon: '🤝',
            initialMessage:
              'やあ、{{userName}}さん、あなたのプロフェッショナルなネットワークを効果的に構築し、維持するお手伝いをします。強固なネットワークはチャンスの扉を開き、キャリアの成長を促進します。\n\n真摯なネットワーキングと関係構築の戦略を一緒に探りましょう。どんなネットワーキングの課題に取り組みたいですか？',
            submodules: {
              relationship_building: {
                title: '本物のプロフェッショナルな関係を築きたい',
                description: '持続可能なプロフェッショナルな繋がりを作る',
              },
              visibility: {
                title: '組織内での可視性を高めたい',
                description:
                  'あなたのプロフェッショナルな存在感とブランドを向上させる',
              },
              mentorship: {
                title: 'メンターを見つけ、関わりたい',
                description: '意味のあるメンタリング関係を築く',
              },
            },
          },
          performance: {
            title: 'チームのパフォーマンスを最適化する',
            description: '効果的なフィードバックを通じてチームの成功を促進する',
            initialMessage:
              'やあ、{{userName}}さん、今日は効果的なフィードバックとコーチングを通じてチームのパフォーマンスを最適化することに注力します。建設的なフィードバックを提供し、チームの成長を導く能力はその成功の鍵です。共に、明確で支援的かつ行動指向のフィードバックを練習しましょう。\nチームの持続的な成長は、リーダーシップの違いによって大きく左右されます。チームへのフィードバックについてどう感じていますか？具体的なパフォーマンスの課題はありますか？',
            submodules: {
              practice_performance_feedback: {
                title:
                  '成長フィードバックと低パフォーマンスについて議論するサポートがほしい',
                description: '建設的なフィードバックを効果的に伝える',
              },
            },
          },
          crisis: {
            title: '危機と従業員関係を乗り越える',
            description: '敏感な状況や対立を適切に対処する',
            initialMessage:
              'こんにちは、{{userName}}さん。本日のセッションは、敏感な状況や従業員関係を慎重かつ自信を持って対応する方法についてです。対立の解決、チーム内の緊張の緩和、危機管理など、これらの会話は感情的知性、共感、明確なコミュニケーションが求められます。\nリーダーとして、これらの問題に冷静かつ建設的に取り組むことが重要です。チームダイナミクスや現在抱えている課題で、一緒に対処したいことはありますか？',
            submodules: {
              practice_conflict_resolution: {
                title: 'チーム内の対立解決の練習をしたい',
                description: 'チーム内の対立を仲裁し、解決する',
              },
            },
          },
          wellbeing: {
            title: 'チームの作業負荷とウェルビーイングを管理する',
            description:
              'パフォーマンスを維持しながらチームの健康をサポートする',
            initialMessage:
              'やあ、{{userName}}さん、今日はチームの作業負荷とウェルビーイングの管理に焦点を当てます。パフォーマンスとメンタルヘルスのバランスはチームの成功に不可欠であり、リーダーとしてサポートする環境を整えることが求められます。チームの状況確認、境界の設定、メンタルヘルスのサポート戦略について一緒に考えましょう。',
            submodules: {
              practice_mental_health: {
                title: 'メンタルヘルスに関する会話のためのヒントがほしい',
                description: 'ウェルビーイングに関する議論を慎重に進める',
              },
            },
          },
        },
        namedialog: {
          title: 'お名前は？',
          placeholder: '名前を入力してください',
          ctalabel: 'コーチングセッションを開始',
        },
        roleplay_cta: {
          practice: '練習する',
          learn: '学ぶ',
          generating: '会話に基づいてロールプレイシナリオを生成中…',
        },
        scenario_generation_messages: {
          message1: '会話に基づいてパーソナライズされた練習シナリオを生成中…',
          message2: 'あなたの好みを分析中…',
          message3: 'もう少し、最終調整中…',
          message4: '最良の詳細でシナリオを確定中…',
          message5: 'あと少しお待ちください、準備中…',
        },
        network: {
          connection_quality: '接続品質',
          latency: 'レイテンシ',
        },
        avatar: {
          speaking: '発言中',
          listening: '傾聴中',
        },
        transcript: {
          ai_coach: 'AIコーチ',
          you: 'あなた',
        },
        error: {
          title: '接続エラー',
          message:
            'AIコーチに接続できません。インターネット接続を確認して再試行してください。',
          try_again: '再試行',
        },
        metrics: {
          title: 'セッションメトリクス',
          speaking_time: '発言時間',
          response_speed: '応答速度',
          turn_taking: 'ターンテイキング',
        },
        loading: {
          title: {
            connecting: 'AIコーチに接続中',
            ending: 'フィードバック生成中',
            loading: 'シナリオを読み込み中',
            error: 'エラー',
          },
          message: {
            connecting: '安全な接続を確立中…',
            ending: 'あなたのパフォーマンスを分析中…',
            loading: 'あなた専用の練習セッションを準備中…',
            error:
              'AIコーチに接続できません。インターネット接続を確認して再試行してください。',
          },
        },
        scenario: {
          phase: {
            pre_start: '開始準備完了',
            intro: 'イントロダクション',
            main: 'メイン会話',
          },
          details: {
            objective: '目的',
            framework: 'フレームワーク',
            no_framework: 'フレームワークなし',
            view_details: 'フレームワークの詳細を見る',
            title: 'シナリオの詳細',
            summary: '概要',
          },
          buttons: {
            start: '練習を開始',
            end: 'セッション終了',
          },
        },
        framework_modal: {
          close: '閉じる',
        },
        roleplay_area: {
          scenario_section: {
            title: 'シナリオ',
          },
          ai_coach: {
            alt: 'AIコーチ',
          },
        },
        control_panel: {
          skip_intro: 'イントロをスキップ',
          end_session: 'セッション終了',
          ai_status: {
            speaking: 'AIコーチが発言中',
            listening: 'AIコーチが傾聴中',
          },
        },
        connection_monitor: {
          messages: {
            offline: 'インターネット接続なし',
            good: '強い接続',
            fair: '普通の接続 - 遅延が発生する可能性があります',
            poor: '弱い接続 - 遅延が発生する可能性があります',
            checking: '接続を確認中…',
          },
        },
      },
    },
  },
  zh: {
    translation: {
      coach: {
        landing: {
          title: '您的個人領導力教練',
          title1: '您的個人',
          title2: '領導力教練',
          subtitle: '在您的Slack中',
          description:
            '獲取個性化指導，練習困難對話，並接收即時反饋，成為更優秀的領導者。',
          startTrial: '開始免費試用',
          requestAccess: {
            title: '申請訪問',
            subtitle: '輸入您的電子郵件以開始使用我們的AI領導力教練',
            button: '申請訪問',
            inputPlaceholder: '輸入您的電子郵件',
          },
          features: {
            personalCoach: {
              title: '個人AI教練',
              description: '從理解您特定挑戰和目標的AI獲得個性化指導。',
            },
            realScenarios: {
              title: '真實場景',
              description:
                '通過根據您的經驗水平和當前情況量身定制的真實場景進行練習。',
            },
            immediateFeedback: {
              title: '即時反饋',
              description:
                '在每次會話後獲得即時、可行的反饋，提升您的領導能力。',
            },
          },
        },
        categories: {
          title: '您最關心的是什麼？',
          subtitle: '獲取管理團隊的個性化指導和建議',
          nameDialog: {
            title: '讓我們開始吧',
            inputPlaceholder: '輸入您的姓名',
            button: '繼續',
          },
        },
        chat: {
          sidebar: {
            home: '首頁',
          },
          typing: 'AI教練正在輸入...',
          inputPlaceholder: '輸入您的訊息...',
          establishConnection: '正在建立安全連接...',
          prepareSession: '正在準備您的個性化練習會話...',
          sendButton: '發送',
          startPractice: '開始練習',
          endSession: '結束會話',
          loadingScenario: '正在載入場景...',
          endingMessages: {
            analyzingPerformance: '正在分析您的表現...',
            reviewActions: '正在審查您的行動並提供見解...',
            generatingFeedback: '正在生成反饋和建議...',
            finalizingAnalysis: '正在完成分析並準備反饋...',
          },
          feedbackOptions: {
            practice_again: {
              label: '🔄 再次練習',
              description: '開始另一個角色扮演會話',
            },
            discuss_feedback: {
              label: '💬 討論反饋',
              description: '獲取有關特定反饋點的更多詳情',
            },
          },
          feedback: {
            title: '評價您的體驗',
            subtitle: '角色扮演對提高您的信心有多大幫助？',
            additionalFeedback: '額外反饋',
            feedbackPlaceholder: '在此處輸入您的評論...',
            submit: '提交',
            close: '關閉',
            overview: '讓我們回顧您的角色扮演表現。\n\n{{scenarioOverview}}',
            strengths_header: '主要優勢',
            improvements_header: '需要改進的領域',
            next_steps_header: '建議的下一步行動',
            final_prompt: '您是否想再次練習或討論反饋中的任何特定方面？',
          },
        },
        roleplay: {
          preparation: '正在準備您的個性化練習會話...',
          connecting: '正在連接到AI教練',
          starting: '正在開始練習會話...',
          ending: '正在生成反饋',
          scenarios: {
            title: '場景：',
            goal: '目標：',
            objective: '目標',
          },
          controls: {
            skipIntro: '跳過介紹',
            endPractice: '結束練習',
            restart: '重新開始',
          },
        },
        modules: {
          hiring: {
            title: '準備招聘面試',
            description: '掌握面試技巧和候選人評估',
            initialMessage: `嗨，{{userName}}，今天很高興能與您一起掌握面試藝術！無論您是首次進行面試，還是想要提升您的技巧，我都在這裡幫助您。我們將深入探討有效評估候選人的成熟技巧，幫助您通過結構化面試保持一致性，並讓您有機會練習這些關鍵對話。
            面試是一項可能決定團隊成敗的技能，有了合適的工具，您將能夠做出明智、公正的決定。您對下一輪面試有什麼感覺？有哪些特定領域您想要重點關注嗎？`,
            submodules: {
              practice_interview: {
                title: '我想練習進行招聘面試',
                description: '練習結構化面試技巧',
              },
              behavioral_interview: {
                title: '我想練習行為面試',
                description: '學習評估過去經驗和行為以預測未來表現',
              },
              technical_interview: {
                title: '我想練習技術面試',
                description: '培養評估技術能力和解決問題能力的技巧',
              },
              culture_fit: {
                title: '我想評估文化契合度',
                description: '練習評估候選人與組織文化的契合度',
              },
            },
          },
          onboarding: {
            title: '新員工入職',
            description: '設定期望並協調新團隊成員',
            icon: '🚀',
            initialMessage: `您好，{{userName}}，歡迎參加入職指導會話！入職是新員工對您團隊的第一印象，也為他們的成功奠定基礎。今天，我們將致力於協調期望、設定明確目標，並幫助您建立積極有效的入職流程。
            作為領導者，讓新員工從第一天起就感到被支持並為成功做好準備至關重要。您對當前的入職流程有何感受？是否有任何特定挑戰或您想改進的領域？`,
            submodules: {
              practice_expectations: {
                title: '我想練習與新團隊成員協調績效期望',
                description: '設定明確的期望和目標',
              },
              team_integration: {
                title: '我想練習向團隊介紹新員工並建立團隊動態',
                description: '從第一天起培養積極的團隊關係和協作',
              },
              feedback_mechanisms: {
                title: '我想練習與新員工建立反饋渠道',
                description: '創建開放的溝通和反饋循環',
              },
            },
          },
          career: {
            title: '引導職業對話',
            description: '指導職業發展討論',
            initialMessage: `嗨，{{userName}}，很高興今天能幫助您與團隊成員進行有影響力的職業對話！職業發展對於保持高績效者的參與度、積極性和在其角色中的成長至關重要。我將引導您進行關鍵一對一討論的策略，提供可行的反饋，並確保您能培養成長文化。
            這些對話可能是團隊成員職業旅程的轉折點。在準備下一次職業對話時，您心中有哪些想法？有什麼特定的目標或疑慮嗎？`,
            submodules: {
              practice_hipo_discuss: {
                title: '我想為與高潛力團隊成員的一對一會談做準備',
                description: '指導高績效者的職業發展',
              },
              promo_conversations: {
                title: '我想練習討論團隊成員自己提出的晉升準備情況',
                description: '處理晉升期望',
              },
              growth_plans_discussion: {
                title: '我想幫助團隊成員制定明確的發展計劃',
                description: '共同創建與職業目標一致的可行成長計劃',
              },
            },
          },
          personalCareer: {
            title: '加速您的職業發展',
            description: '引導您的專業成長和發展',
            icon: '⭐',
            initialMessage: `嗨，{{userName}}，我很高興今天能幫助您加速職業成長！無論您是希望在當前角色中晉升，還是探索新機會，我們都將共同制定策略，幫助您實現專業目標。

  職業發展是一段持續成長和自我發現的旅程。今天您希望關注職業的哪些方面？`,
            submodules: {
              strategic_planning: {
                title: '我想創建戰略性職業規劃',
                description: '為您的職業晉升制定路線圖',
              },
              promotion_readiness: {
                title: '我想為下一次晉升做準備',
                description: '培養技能和提高曝光度以促進職業進步',
              },
              skill_development: {
                title: '我想確定需要發展的關鍵技能',
                description: '評估並規劃您的專業發展',
              },
            },
          },
          influence: {
            title: '建立影響力與影響',
            description: '提升您的溝通能力和領導風範',
            icon: '🎯',
            initialMessage: `您好，{{userName}}，今天我們將通過有效溝通來增強您的影響力。無論是公開演講、談判還是激勵他人，這些技能對職業成功都至關重要。

  讓我們探討如何提升您的存在感並產生更強影響力。您希望解決哪些具體溝通挑戰？`,
            submodules: {
              public_speaking: {
                title: '我想提高我的公開演講技巧',
                description: '掌握自信地向群體展示的能力',
              },
              negotiation: {
                title: '我想提升我的談判能力',
                description: '學習有效的談判技巧',
              },
              stakeholder: {
                title: '我想更好地影響利益相關者',
                description: '建立關係並管理期望',
              },
            },
          },
          executivePresence: {
            title: '培養高管風範',
            description: '建立威信和戰略思維能力',
            initialMessage: `嗨，{{userName}}，今天我們將專注於發展您的高管風範和戰略思維。無論您是瞄準高級領導職位，還是希望被視為更具戰略性的貢獻者，這些技能對長期職業成功都至關重要。

            讓我們探討如何提升您的高管風範和戰略思維。您希望關注哪些具體方面？`,
            submodules: {
              strategic_thinking: {
                title: '我想強化我的戰略思維',
                description: '發展並傳達戰略視角',
              },
              executive_communication: {
                title: '我想更有效地與高級領導溝通',
                description: '掌握高管級別的溝通',
              },
              business_acumen: {
                title: '我想提升我的商業和財務敏銳度',
                description: '建立更強的商業和財務理解',
              },
            },
          },
          networking: {
            title: '拓展您的人脈網絡',
            description: '建立有意義的專業關係',
            icon: '🤝',
            initialMessage: `嗨，{{userName}}，我在這裡幫助您有效建立和培養專業網絡。強大的人脈網絡為機會打開大門，加速職業成長。

  讓我們探索真誠網絡建設和關係培養的策略。您希望專注於哪些網絡挑戰？`,
            submodules: {
              relationship_building: {
                title: '我想建立真誠的專業關係',
                description: '創建持久的專業聯繫',
              },
              visibility: {
                title: '我想提高我在組織中的曝光度',
                description: '增強您的專業形象和品牌',
              },
              mentorship: {
                title: '我想尋找並與導師互動',
                description: '發展有意義的導師關係',
              },
            },
          },
          performance: {
            title: '優化團隊績效',
            description: '通過有效反饋推動團隊成功',
            initialMessage: `嗨，{{userName}}，今天我們將專注於通過有效反饋和指導優化團隊績效。您提供建設性反饋並引導團隊度過成長時刻的能力對他們的成功至關重要。我們將一起練習提供清晰、支持性且行動導向的反饋。
            強大的績效是持續發展的結果，您的領導能夠產生重大影響。您對向團隊提供反饋有何感受？今天有沒有特定的績效挑戰您想解決？`,
            submodules: {
              practice_performance_feedback: {
                title: '我想幫助討論成長反饋和績效不佳問題',
                description: '有效提供建設性反饋',
              },
            },
          },
          crisis: {
            title: '處理危機與員工關係',
            description: '處理敏感情況和衝突',
            initialMessage: `您好，{{userName}}，今天的會話是關於謹慎且自信地處理敏感情況和員工關係。無論是解決衝突、緩解團隊緊張關係還是管理危機，這些對話都需要情商、同理心和清晰溝通。
            作為領導者，用冷靜和建設性的方法直面這些問題很重要。關於團隊動態或您希望解決的任何持續問題，您有什麼想法？`,
            submodules: {
              practice_conflict_resolution: {
                title: '我想練習解決團隊成員之間的衝突',
                description: '調解和解決團隊衝突',
              },
            },
          },
          wellbeing: {
            title: '管理團隊工作負荷與健康',
            description: '在保持績效的同時支持團隊健康',
            initialMessage: `嗨，{{userName}}，今天我們將專注於管理團隊工作負荷和健康。平衡績效和心理健康對團隊成功至關重要，作為領導者，您的角色是創造一個支持性環境，促進成長和韌性。我們將探討與團隊溝通、設定邊界和支持心理健康的策略。`,
            submodules: {
              practice_mental_health: {
                title: '我想獲得指導心理健康相關對話的技巧',
                description: '謹慎處理健康討論',
              },
            },
          },
        },
        namedialog: {
          title: '您的名字是？',
          placeholder: '輸入您的名字',
          ctalabel: '開始指導會話',
        },
        roleplay_cta: {
          practice: '練習',
          learn: '學習',
          generating: '正在根據我們的對話生成角色扮演場景...',
        },
        scenario_generation_messages: {
          message1: '正在根據我們的對話生成個性化練習場景...',
          message2: '我們正在分析您的偏好...',
          message3: '即將完成，正在微調所有內容...',
          message4: '正在用最佳細節完善您的場景...',
          message5: '再等片刻，正在安排一切...',
        },
        network: {
          connection_quality: '連接品質',
          latency: '延遲',
        },
        avatar: {
          speaking: '正在講話',
          listening: '正在傾聽',
        },
        transcript: {
          ai_coach: 'AI教練',
          you: '您',
        },
        error: {
          title: '連接錯誤',
          message: '無法連接到AI教練。請檢查您的網路連接並重試。',
          try_again: '重試',
        },
        metrics: {
          title: '會話指標',
          speaking_time: '發言時間',
          response_speed: '回應速度',
          turn_taking: '輪流發言',
        },
        loading: {
          title: {
            connecting: '正在連接AI教練',
            ending: '正在生成反饋',
            loading: '正在載入場景',
            error: '錯誤',
          },
          message: {
            connecting: '正在建立安全連接...',
            ending: '正在分析您的表現...',
            loading: '正在準備您的個性化練習會話...',
            error: '無法連接到AI教練。請檢查您的網路連接並重試。',
          },
        },
        scenario: {
          phase: {
            pre_start: '準備開始',
            intro: '介紹',
            main: '主要對話',
          },
          details: {
            objective: '目標',
            framework: '框架',
            no_framework: '無框架',
            view_details: '查看框架詳情',
            title: '場景詳情',
            summary: '摘要',
          },
          buttons: {
            start: '開始練習',
            end: '結束會話',
          },
        },
        framework_modal: {
          close: '關閉',
        },
        roleplay_area: {
          scenario_section: {
            title: '場景',
          },
          ai_coach: {
            alt: 'AI教練',
          },
        },
        control_panel: {
          skip_intro: '跳過介紹',
          end_session: '結束會話',
          ai_status: {
            speaking: 'AI正在講話',
            listening: 'AI正在傾聽',
          },
        },
        connection_monitor: {
          messages: {
            offline: '無網路連接',
            good: '強連接',
            fair: '中等連接 - 可能會有延遲',
            poor: '弱連接 - 可能會有延遲',
            checking: '正在檢查連接...',
          },
        },
      },
    },
  },
  tl: {
    translation: {
      coach: {
        landing: {
          title: 'Inyong Personal na Leadership Coach',
          title1: 'Inyong Personal na',
          title2: 'Leadership Coach',
          subtitle: 'sa Inyong Slack',
          description:
            'Makakuha ng personalized na coaching, mag-practice ng mga mahirap na pag-uusap, at makatanggap ng agarang feedback upang maging mas magaling na lider.',
          startTrial: 'Simulan ang Free Trial',
          requestAccess: {
            title: 'Humingi ng access',
            subtitle:
              'Ilagay ang inyong email upang magsimula sa aming AI leadership coach',
            button: 'Humingi ng access',
            inputPlaceholder: 'Ilagay ang inyong email',
          },
          features: {
            personalCoach: {
              title: 'Personal na AI Coach',
              description:
                'Makakuha ng personalized na coaching mula sa AI na nakakaintindi sa inyong mga specific na hamon at layunin.',
            },
            realScenarios: {
              title: 'Mga Tunay na Scenario',
              description:
                'Mag-practice sa mga tunay na scenario na ginawa para sa inyong antas ng karanasan at kasalukuyang sitwasyon.',
            },
            immediateFeedback: {
              title: 'Agarang Feedback',
              description:
                'Makakuha ng agarang, actionable na feedback pagkatapos ng bawat session upang mapahusay ang inyong mga leadership skills.',
            },
          },
        },
        categories: {
          title: 'Ano ang pinaka-naging dahilan ng inyong pag-aalala?',
          subtitle:
            'Makakuha ng personalized na guidance at advice para sa paghandle ng team',
          nameDialog: {
            title: "Tara na't magsimula",
            inputPlaceholder: 'Ilagay ang inyong pangalan',
            button: 'Magpatuloy',
          },
        },
        chat: {
          sidebar: {
            home: 'Home',
          },
          typing: 'Nagte-type ang AI coach...',
          inputPlaceholder: 'I-type ang inyong mensahe...',
          establishConnection: 'Nagse-set up ng secure connection...',
          prepareSession:
            'Naghahanda ng inyong personalized practice session...',
          sendButton: 'Ipadala',
          startPractice: 'Simulan ang Practice',
          endPractice: 'Tapusin ang Practice',
          feedbackButton: 'Tingnan ang Feedback',
          thinking: 'Nag-iisip ang AI...',
          startConversation: 'Simulan ang Pag-uusap',
          connectionError: 'May problema sa koneksyon. Pakisubukan ulit.',
          microphoneError:
            'Hindi ma-access ang microphone. Pakitingnan ang inyong mga settings.',
          connecting: 'Kumukonekta...',
        },
        persona: {
          title: 'Ano ang uri ng kliyente ang gusto ninyong makausap?',
          subtitle: 'Piliin ang persona na gusto ninyong i-practice',
        },
        feedback: {
          header: {
            title: 'Practice Session Feedback',
            subtitle: 'Narito ang inyong performance analysis',
          },
          sections: {
            summary: 'Buod',
            detailed: 'Detalyadong Analysis',
            transcript: 'Transcript',
          },
          stats: {
            duration: 'Tagal ng Session',
            exchanges: 'Mga Palitan ng Usapan',
            keyPoints: 'Mga Pangunahing Punto',
          },
          noFeedback: 'Walang feedback na available para sa session na ito.',
        },
        voice: {
          status: {
            connecting: 'Kumukonekta sa voice service...',
            connected: 'Nakakonekta sa voice',
            disconnected: 'Hindi nakakonekta sa voice',
            error: 'May error sa voice connection',
            speaking: 'Nagsasalita ang AI',
            listening: 'Nakikinig ang AI',
          },
        },
        connection_monitor: {
          messages: {
            offline: 'Walang internet connection',
            good: 'Malakas na connection',
            fair: 'Katamtamang connection - maaaring may delay',
            poor: 'Mahinang connection - maaaring may delay',
            checking: 'Sinusuri ang connection...',
          },
        },
      },
    },
  },
  sw: {
    translation: {
      coach: {
        landing: {
          title: 'Kocha Wako Binafsi wa Uongozi',
          title1: 'Kocha Wako',
          title2: 'Binafsi wa Uongozi',
          subtitle: 'katika Slack yako',
          description:
            'Pata ukufunzi uliobinafsishwa, fanya mazoezi ya mazungumzo magumu, na upokee maoni ya haraka ili kuwa kiongozi bora.',
          startTrial: 'Anza Jaribio Bila Malipo',
          requestAccess: {
            title: 'Omba ufikiaji',
            subtitle:
              'Weka barua pepe yako ili kuanza na kocha wetu wa uongozi wa AI',
            button: 'Omba ufikiaji',
            inputPlaceholder: 'Weka barua pepe yako',
          },
          features: {
            personalCoach: {
              title: 'Kocha wa AI Binafsi',
              description:
                'Pata ukufunzi uliobinafsishwa kutoka kwa AI inayofahamu changamoto zako na malengo yako maalum.',
            },
            realScenarios: {
              title: 'Hali Halisi',
              description:
                'Fanya mazoezi na hali halisi zinazolingana na uzoefu na hali yako ya sasa.',
            },
            immediateFeedback: {
              title: 'Maoni ya Haraka',
              description:
                'Pokea maoni ya papo hapo yanayoweza kutekelezwa baada ya kila kikao ili kuboresha ujuzi wako wa uongozi.',
            },
          },
        },
        categories: {
          title: 'Ni jambo gani liko akilini mwako?',
          subtitle:
            'Pata ukufunzi na mwongozo uliobinafsishwa wa kusimamia timu yako',
          nameDialog: {
            title: 'Tuanze',
            inputPlaceholder: 'Andika jina lako',
            button: 'Endelea',
          },
        },
        chat: {
          sidebar: {
            home: 'Nyumbani',
          },
          typing: 'Kocha wa AI anaandika...',
          inputPlaceholder: 'Andika ujumbe wako...',
          establishConnection: 'Inaweka muunganisho salama...',
          prepareSession:
            'Inatayarisha kikao chako cha mazoezi kilichobinafsishwa...',
          sendButton: 'Tuma',
          startPractice: 'Anza Mazoezi',
          endPractice: 'Maliza Mazoezi',
          feedbackButton: 'Angalia Maoni',
          thinking: 'AI inafikiri...',
          startConversation: 'Anza Mazungumzo',
          connectionError: 'Tatizo la muunganisho. Tafadhali jaribu tena.',
          microphoneError:
            'Haipati kipaza sauti. Tafadhali kagua mipangilio yako.',
          connecting: 'Inaunganisha...',
        },
        persona: {
          title: 'Ungependa kuzungumza na mteja wa aina gani?',
          subtitle: 'Chagua hulka unayotaka kufanyia mazoezi',
        },
        feedback: {
          header: {
            title: 'Maoni ya Kikao cha Mazoezi',
            subtitle: 'Hapa kuna uchanganuzi wa utendaji wako',
          },
          sections: {
            summary: 'Muhtasari',
            detailed: 'Uchambuzi wa Kina',
            transcript: 'Transkripti',
          },
          stats: {
            duration: 'Muda wa Kikao',
            exchanges: 'Mabadilishano ya Mazungumzo',
            keyPoints: 'Vipengele Muhimu',
          },
          noFeedback: 'Hakuna maoni yanayopatikana kwa kikao hiki.',
        },
        voice: {
          status: {
            connecting: 'Inaunganisha kwa huduma ya sauti...',
            connected: 'Imeunganishwa kwa sauti',
            disconnected: 'Haijaunganishwa kwa sauti',
            error: 'Hitilafu ya muunganisho wa sauti',
            speaking: 'AI inazungumza',
            listening: 'AI inasikiliza',
          },
        },
        loading: {
          title: {
            connecting: 'Inaunganisha kwa Kocha wa AI',
            ending: 'Inazalisha Maoni',
            loading: 'Inapakia Hali',
            error: 'Hitilafu',
          },
          message: {
            connecting: 'Inaweka muunganisho salama...',
            ending: 'Inachambua utendaji wako...',
            loading:
              'Inatayarisha kikao chako cha mazoezi kilichobinafsishwa...',
            error:
              'Haikuweza kuunganisha na Kocha wa AI. Tafadhali kagua muunganisho wako wa intaneti na ujaribu tena.',
          },
        },
        connection_monitor: {
          messages: {
            offline: 'Hakuna muunganisho wa intaneti',
            good: 'Muunganisho thabiti',
            fair: 'Muunganisho wa wastani - huenda kukawa na ucheleweshaji',
            poor: 'Muunganisho dhaifu - huenda kukawa na ucheleweshaji',
            checking: 'Inakagua muunganisho...',
          },
        },
      },
    },
  },
  ms: {
    translation: {
      coach: {
        landing: {
          title: 'Jurulatih Kepimpinan Peribadi Anda',
          title1: 'Jurulatih Kepimpinan',
          title2: 'Peribadi Anda',
          subtitle: 'dalam Slack Anda',
          description:
            'Dapatkan bimbingan yang diperibadikan, amalkan perbualan yang sukar, dan terima maklum balas segera untuk menjadi pemimpin yang lebih baik.',
          startTrial: 'Mulakan Percubaan Percuma',
          requestAccess: {
            title: 'Minta akses',
            subtitle:
              'Masukkan e-mel anda untuk bermula dengan jurulatih kepimpinan AI kami',
            button: 'Minta akses',
            inputPlaceholder: 'Masukkan e-mel anda',
          },
          features: {
            personalCoach: {
              title: 'Jurulatih AI Peribadi',
              description:
                'Dapatkan bimbingan yang diperibadikan daripada AI yang memahami cabaran dan matlamat khusus anda.',
            },
            realScenarios: {
              title: 'Senario Sebenar',
              description:
                'Berlatih dengan senario realistik yang disesuaikan dengan tahap pengalaman dan situasi semasa anda.',
            },
            immediateFeedback: {
              title: 'Maklum Balas Segera',
              description:
                'Terima maklum balas segera yang boleh diambil tindakan selepas setiap sesi untuk meningkatkan kemahiran kepimpinan anda.',
            },
          },
        },
        categories: {
          title: 'Apa yang paling penting bagi anda?',
          subtitle:
            'Akses bimbingan dan panduan yang diperibadikan untuk mengurus pasukan anda',
          nameDialog: {
            title: 'Mari kita mulakan',
            inputPlaceholder: 'Masukkan nama anda',
            button: 'Teruskan',
          },
        },
        chat: {
          sidebar: {
            home: 'Laman Utama',
          },
          typing: 'Jurulatih AI sedang menaip...',
          inputPlaceholder: 'Taip mesej anda...',
          establishConnection: 'Mewujudkan sambungan selamat...',
          prepareSession: 'Menyediakan sesi latihan peribadi anda...',
          sendButton: 'Hantar',
          startPractice: 'Mulakan Latihan',
          endPractice: 'Tamatkan Latihan',
          feedbackButton: 'Lihat Maklum Balas',
          thinking: 'AI sedang berfikir...',
          startConversation: 'Mulakan Perbualan',
          connectionError: 'Masalah sambungan. Sila cuba lagi.',
          microphoneError:
            'Tidak dapat mengakses mikrofon. Sila semak tetapan anda.',
          connecting: 'Menyambung...',
        },
        persona: {
          title: 'Apakah jenis klien yang anda ingin bercakap?',
          subtitle: 'Pilih persona yang anda ingin amalkan',
        },
        feedback: {
          header: {
            title: 'Maklum Balas Sesi Latihan',
            subtitle: 'Berikut adalah analisis prestasi anda',
          },
          sections: {
            summary: 'Ringkasan',
            detailed: 'Analisis Terperinci',
            transcript: 'Transkrip',
          },
          stats: {
            duration: 'Tempoh Sesi',
            exchanges: 'Pertukaran Perbualan',
            keyPoints: 'Perkara Utama',
          },
          noFeedback: 'Tiada maklum balas tersedia untuk sesi ini.',
        },
        voice: {
          status: {
            connecting: 'Menyambung ke perkhidmatan suara...',
            connected: 'Bersambung ke suara',
            disconnected: 'Terputus dari suara',
            error: 'Ralat sambungan suara',
            speaking: 'AI sedang bercakap',
            listening: 'AI sedang mendengar',
          },
        },
        connection_monitor: {
          messages: {
            offline: 'Tiada sambungan internet',
            good: 'Sambungan yang kuat',
            fair: 'Sambungan sederhana - mungkin ada kelewatan',
            poor: 'Sambungan lemah - mungkin ada kelewatan',
            checking: 'Memeriksa sambungan...',
          },
        },
      },
    },
  },
  ar: {
    translation: {
      coach: {
        landing: {
          title: 'مدربك القيادي الشخصي',
          title1: 'مدربك',
          title2: 'القيادي الشخصي',
          subtitle: 'في Slack الخاص بك',
          description:
            'احصل على تدريب شخصي، مارس المحادثات الصعبة، واحصل على ملاحظات فورية لتصبح قائداً أفضل.',
          startTrial: 'ابدأ التجربة المجانية',
        },
        chat: {
          sidebar: {
            home: 'الرئيسية',
          },
          typing: 'المدرب الذكي يكتب...',
          inputPlaceholder: 'اكتب رسالتك...',
          establishConnection: 'إنشاء اتصال آمن...',
          prepareSession: 'تحضير جلسة التمرين الشخصية...',
          sendButton: 'إرسال',
          startPractice: 'بدء التمرين',
          endSession: 'إنهاء الجلسة',
          loadingScenario: 'جاري تحميل السيناريو...',
        },
      },
      agent: {
        title: {
          elevenlabs: 'وكيل المحادثة',
        },
        status: {
          connecting: 'جاري الاتصال...',
          connected: 'متصل',
          speaking: 'الذكاء الاصطناعي يتحدث',
          listening: 'الذكاء الاصطناعي يستمع',
        },
      },
      connection_monitor: {
        messages: {
          offline: 'لا يوجد اتصال بالإنترنت',
          good: 'اتصال قوي',
          fair: 'اتصال متوسط - قد يكون هناك تأخير',
          poor: 'اتصال ضعيف - قد يكون هناك تأخير',
          checking: 'جاري فحص الاتصال...',
        },
      },
    },
  },
  vi: {
    translation: {
      coach: {
        landing: {
          title: 'Huấn luyện viên lãnh đạo cá nhân của bạn',
          title1: 'Huấn luyện viên lãnh đạo',
          title2: 'cá nhân của bạn',
          subtitle: 'trên Slack',
          description:
            'Nhận huấn luyện cá nhân hóa, thực hành các cuộc trò chuyện khó và nhận phản hồi ngay lập tức để trở thành nhà lãnh đạo tốt hơn.',
          startTrial: 'Bắt đầu dùng thử miễn phí',
          requestAccess: {
            title: 'Yêu cầu truy cập',
            subtitle: 'Nhập email để bắt đầu với AI huấn luyện viên lãnh đạo',
            button: 'Yêu cầu truy cập',
            inputPlaceholder: 'Nhập email của bạn',
          },
          features: {
            personalCoach: {
              title: 'AI Coach cá nhân',
              description:
                'Nhận huấn luyện cá nhân hóa từ AI hiểu rõ thách thức và mục tiêu của bạn.',
            },
            realScenarios: {
              title: 'Thực hành theo kịch bản',
              description:
                'Luyện tập qua các kịch bản dựa trên tình huống thực tế để phát triển năng lực cần thiết.',
            },
            immediateFeedback: {
              title: 'Phản hồi ngay lập tức',
              description:
                'Nhận phản hồi tức thì và có thể hành động sau mỗi phiên để nâng cao kỹ năng lãnh đạo.',
            },
          },
        },
        categories: {
          title: 'Bạn đang quan tâm điều gì nhất?',
          subtitle:
            'Trải nghiệm huấn luyện cá nhân hóa để quản lý đội nhóm hiệu quả.',
          nameDialog: {
            title: 'Hãy bắt đầu',
            inputPlaceholder: 'Nhập tên của bạn',
            button: 'Tiếp tục',
          },
        },
        chat: {
          sidebar: {
            home: 'Trang chủ',
          },
          typing: 'AI Coach đang nhập...',
          inputPlaceholder: 'Nhập tin nhắn của bạn...',
          establishConnection: 'Đang thiết lập kết nối bảo mật...',
          prepareSession: 'Đang chuẩn bị phiên thực hành cá nhân...',
          sendButton: 'Gửi',
          startPractice: 'Bắt đầu thực hành',
          endSession: 'Kết thúc phiên',
          loadingScenario: 'Đang tải kịch bản...',
          endingMessages: {
            analyzingPerformance: 'Đang phân tích hiệu suất của bạn...',
            reviewActions: 'Đang xem xét các hành động và cung cấp nhận xét...',
            generatingFeedback: 'Đang tạo phản hồi và khuyến nghị...',
            finalizingAnalysis:
              'Đang hoàn thiện phân tích và chuẩn bị phản hồi...',
          },
          feedbackOptions: {
            practice_again: {
              label: '🔄 Thực hành lại',
              description: 'Bắt đầu phiên thực hành mới',
            },
            discuss_feedback: {
              label: '💬 Thảo luận phản hồi',
              description: 'Tìm hiểu chi tiết về các điểm phản hồi cụ thể',
            },
          },
          feedback: {
            title: 'Đánh giá trải nghiệm của bạn',
            subtitle: 'Buổi thực hành đã giúp bạn tự tin hơn như thế nào?',
            additionalFeedback: 'Phản hồi bổ sung',
            feedbackPlaceholder: 'Nhập ý kiến của bạn...',
            submit: 'Gửi',
            close: 'Đóng',
            overview:
              'Hãy cùng xem lại hiệu suất thực hành của bạn.\n\n{{scenarioOverview}}',
            strengths_header: 'Điểm mạnh chính',
            improvements_header: 'Lĩnh vực cần cải thiện',
            next_steps_header: 'Bước tiếp theo được đề xuất',
            final_prompt:
              'Bạn muốn thực hành lại hay thảo luận về phần phản hồi cụ thể nào?',
          },
        },
        roleplay: {
          preparation: 'Đang chuẩn bị phiên thực hành cá nhân...',
          connecting: 'Đang Kết Nối AI Coach',
          starting: 'Đang bắt đầu phiên thực hành...',
          ending: 'Đang Tạo Phản Hồi',
          scenarios: {
            title: 'Kịch bản:',
            goal: 'Mục tiêu:',
            objective: 'Mục tiêu',
          },
          controls: {
            skipIntro: 'Bỏ qua giới thiệu',
            endPractice: 'Kết thúc thực hành',
            restart: 'Bắt đầu lại',
          },
        },
        modules: {
          hiring: {
            title: 'Chuẩn bị phỏng vấn tuyển dụng',
            description: 'Nắm vững kỹ năng phỏng vấn và đánh giá ứng viên',
            initialMessage: `Xin chào {{userName}}, hôm nay tôi sẽ giúp bạn nắm vững kỹ năng phỏng vấn! Dù bạn mới bắt đầu hay muốn nâng cao kỹ năng hiện có, tôi sẽ hướng dẫn bạn các kỹ thuật đánh giá ứng viên hiệu quả, tiến hành phỏng vấn có cấu trúc và thực hành các cuộc trò chuyện quan trọng.
            Phỏng vấn là kỹ năng có thể quyết định sự thành bại của đội nhóm, và với công cụ phù hợp, bạn có thể đưa ra quyết định công bằng và sáng suốt. Bạn cảm thấy thế nào về buổi phỏng vấn tiếp theo? Có lĩnh vực nào bạn muốn tập trung không?`,
            submodules: {
              practice_interview: {
                title: 'Tôi muốn thực hành tiến hành phỏng vấn tuyển dụng',
                description: 'Thực hành kỹ thuật phỏng vấn có cấu trúc',
              },
              behavioral_interview: {
                title: 'Tôi muốn thực hành phỏng vấn hành vi',
                description:
                  'Học cách dự đoán hiệu suất tương lai qua kinh nghiệm và hành vi trong quá khứ',
              },
              technical_interview: {
                title: 'Tôi muốn thực hành phỏng vấn kỹ thuật',
                description:
                  'Phát triển kỹ năng đánh giá năng lực kỹ thuật và giải quyết vấn đề',
              },
              culture_fit: {
                title: 'Tôi muốn đánh giá sự phù hợp văn hóa',
                description:
                  'Thực hành đánh giá sự phù hợp với văn hóa tổ chức',
              },
            },
          },
          onboarding: {
            title: 'Hội nhập nhân viên mới',
            description: 'Thiết lập kỳ vọng và giúp thành viên mới thích nghi',
            icon: '🚀',
            initialMessage: `Xin chào {{userName}}, chào mừng bạn đến với phiên huấn luyện hội nhập! Hội nhập là ấn tượng đầu tiên mà nhân viên mới có về đội nhóm, và là nền tảng cho sự thành công của họ. Hôm nay chúng ta sẽ cùng thực hành điều chỉnh kỳ vọng, thiết lập mục tiêu rõ ràng và xây dựng quy trình hội nhập tích cực hiệu quả.
            Là nhà lãnh đạo, điều quan trọng là làm cho nhân viên mới cảm thấy được hỗ trợ từ ngày đầu tiên. Bạn nghĩ gì về quy trình hội nhập hiện tại? Có thách thức hay lĩnh vực nào bạn muốn cải thiện không?`,
            submodules: {
              practice_expectations: {
                title:
                  'Tôi muốn thực hành điều chỉnh kỳ vọng hiệu suất với thành viên mới',
                description: 'Thiết lập kỳ vọng và mục tiêu rõ ràng',
              },
              team_integration: {
                title:
                  'Tôi muốn thực hành giới thiệu nhân viên mới vào đội nhóm',
                description:
                  'Xây dựng mối quan hệ và hợp tác tích cực từ ngày đầu',
              },
              feedback_mechanisms: {
                title:
                  'Tôi muốn thực hành thiết lập kênh phản hồi với nhân viên mới',
                description: 'Tạo dựng giao tiếp cởi mở và vòng phản hồi',
              },
            },
          },
          career: {
            title: 'Tiến hành đối thoại nghề nghiệp',
            description:
              'Xây dựng kế hoạch phát triển cá nhân với nhân tài chủ chốt',
            initialMessage: `Xin chào {{userName}}, hôm nay tôi sẽ giúp bạn dẫn dắt các cuộc đối thoại nghề nghiệp có tác động! Phát triển nghề nghiệp là yếu tố quan trọng để giữ chân, tạo động lực và giúp nhân viên xuất sắc phát triển trong vai trò. Tôi sẽ hướng dẫn bạn chiến lược dẫn dắt buổi trao đổi 1:1, cung cấp phản hồi hành động được và xây dựng văn hóa phát triển.
            Những cuộc trò chuyện này có thể là bước ngoặt trong hành trình nghề nghiệp của nhân viên. Bạn đang suy nghĩ gì về cuộc đối thoại nghề nghiệp tiếp theo? Có mục tiêu hay mối quan tâm cụ thể nào không?`,
            submodules: {
              practice_hipo_discuss: {
                title:
                  'Tôi muốn được hỗ trợ chuẩn bị buổi 1:1 với nhân viên tiềm năng cao',
                description:
                  'Hướng dẫn phát triển nghề nghiệp cho nhân viên xuất sắc',
              },
              promo_conversations: {
                title:
                  'Tôi muốn thực hành thảo luận về mức độ sẵn sàng thăng tiến',
                description: 'Quản lý kỳ vọng về thăng tiến',
              },
              growth_plans_discussion: {
                title:
                  'Tôi muốn giúp nhân viên xây dựng kế hoạch phát triển rõ ràng',
                description:
                  'Cùng xây dựng kế hoạch phát triển hành động phù hợp với mục tiêu nghề nghiệp',
              },
            },
          },
          personalCareer: {
            title: 'Tăng tốc phát triển nghề nghiệp',
            description: 'Tiếp cận có hệ thống cho sự phát triển chuyên môn',
            initialMessage: `Xin chào {{userName}}, hôm nay chúng ta sẽ tập trung vào việc tăng tốc phát triển nghề nghiệp của bạn! Dù là thăng tiến trong vai trò hiện tại hay khám phá cơ hội mới, chúng ta sẽ cùng xây dựng chiến lược đạt mục tiêu chuyên môn.

            Phát triển nghề nghiệp là hành trình liên tục của sự trưởng thành và khám phá bản thân. Hôm nay bạn muốn tập trung vào khía cạnh nào trong sự nghiệp?`,
            submodules: {
              strategic_planning: {
                title: 'Tôi muốn xây dựng kế hoạch nghề nghiệp chiến lược',
                description:
                  'Phát triển lộ trình cho sự thăng tiến nghề nghiệp',
              },
              promotion_readiness: {
                title: 'Tôi muốn chuẩn bị cho lần thăng tiến tiếp theo',
                description:
                  'Phát triển năng lực và tầm nhìn cho sự thăng tiến',
              },
              skill_development: {
                title: 'Tôi muốn xác định các năng lực cốt lõi cần phát triển',
                description:
                  'Xây dựng và đánh giá kế hoạch phát triển chuyên môn',
              },
            },
          },
          influence: {
            title: 'Xây dựng ảnh hưởng và tác động',
            description: 'Nâng cao giao tiếp và sự hiện diện lãnh đạo',
            initialMessage: `Xin chào {{userName}}, hôm nay chúng ta sẽ tập trung vào việc xây dựng ảnh hưởng và tác động thông qua giao tiếp hiệu quả. Thuyết trình trước đám đông, đàm phán hay truyền cảm hứng cho người khác đều là năng lực cốt lõi cho sự thành công nghề nghiệp.

            Hãy cùng khám phá cách tăng cường sự hiện diện và tạo tác động mạnh mẽ hơn. Bạn muốn tập trung vào thách thức giao tiếp nào?`,
            submodules: {
              public_speaking: {
                title: 'Tôi muốn nâng cao kỹ năng thuyết trình',
                description: 'Nắm vững kỹ năng thuyết trình tự tin trước nhóm',
              },
              negotiation: {
                title: 'Tôi muốn tăng cường năng lực đàm phán',
                description: 'Học các kỹ thuật đàm phán hiệu quả',
              },
              stakeholder: {
                title: 'Tôi muốn tăng ảnh hưởng với các bên liên quan',
                description: 'Xây dựng mối quan hệ và quản lý kỳ vọng',
              },
            },
          },
          executivePresence: {
            title: 'Phát triển phong thái lãnh đạo cấp cao',
            description: 'Xây dựng uy tín và tư duy chiến lược',
            initialMessage: `Xin chào {{userName}}, hôm nay chúng ta sẽ tập trung vào phát triển phong thái lãnh đạo cấp cao và tư duy chiến lược. Dù bạn hướng đến vị trí lãnh đạo cấp cao hay muốn được nhìn nhận như người đóng góp chiến lược, những kỹ năng này rất cần thiết cho sự thành công nghề nghiệp lâu dài.

            Hãy cùng khám phá cách tăng cường phong thái lãnh đạo và tư duy chiến lược. Bạn muốn tập trung vào khía cạnh cụ thể nào?`,
            submodules: {
              strategic_thinking: {
                title: 'Tôi muốn tăng cường tư duy chiến lược',
                description: 'Phát triển và truyền đạt tầm nhìn chiến lược',
              },
              executive_communication: {
                title: 'Tôi muốn giao tiếp hiệu quả hơn với lãnh đạo cấp cao',
                description: 'Nắm vững kỹ năng giao tiếp cấp lãnh đạo',
              },
              business_acumen: {
                title: 'Tôi muốn nâng cao nhạy bén kinh doanh và tài chính',
                description: 'Tăng cường hiểu biết về kinh doanh và tài chính',
              },
            },
          },
          performance: {
            title: 'Tối ưu hóa hiệu suất đội nhóm',
            description: 'Thúc đẩy thành công đội nhóm qua phản hồi hiệu quả',
            initialMessage: `Xin chào {{userName}}, hôm nay chúng ta sẽ tập trung vào tối ưu hóa hiệu suất đội nhóm thông qua phản hồi và huấn luyện hiệu quả. Khả năng cung cấp phản hồi xây dựng và dẫn dắt những khoảnh khắc phát triển cho đội nhóm là chìa khóa thành công. Chúng ta sẽ cùng thực hành đưa ra phản hồi rõ ràng, hỗ trợ và hướng đến hành động.
            Hiệu suất mạnh mẽ là kết quả của sự phát triển liên tục, và sự lãnh đạo của bạn có thể tạo nên sự khác biệt lớn. Bạn cảm thấy thế nào về việc đưa phản hồi cho đội nhóm? Có thách thức hiệu suất nào bạn muốn giải quyết hôm nay?`,
            submodules: {
              practice_performance_feedback: {
                title:
                  'Tôi muốn được hỗ trợ thảo luận về phản hồi phát triển và hiệu suất thấp',
                description: 'Đưa phản hồi xây dựng một cách hiệu quả',
              },
            },
          },
          crisis: {
            title: 'Quản lý xung đột tại nơi làm việc',
            description: 'Giải quyết xung đột trong các tình huống nhạy cảm',
            initialMessage: `Xin chào {{userName}}, phiên hôm nay tập trung vào xử lý các tình huống nhạy cảm và quản lý mối quan hệ nhân viên một cách khéo léo và tự tin. Các cuộc trò chuyện về giải quyết xung đột, xoa dịu căng thẳng nhóm và quản lý khủng hoảng đòi hỏi trí tuệ cảm xúc, sự đồng cảm và giao tiếp rõ ràng.
            Là nhà lãnh đạo, điều quan trọng là đối mặt trực tiếp với những vấn đề này bằng cách tiếp cận bình tĩnh và xây dựng. Có vấn đề nào trong đội nhóm bạn muốn cùng giải quyết không?`,
            submodules: {
              practice_conflict_resolution: {
                title:
                  'Tôi muốn thực hành giải quyết xung đột giữa các thành viên',
                description: 'Hòa giải và giải quyết xung đột trong đội nhóm',
              },
            },
          },
          wellbeing: {
            title: 'Quản lý khối lượng công việc và sức khỏe đội nhóm',
            description:
              'Quản lý cường độ làm việc trong khi duy trì hiệu suất',
            initialMessage: `Xin chào {{userName}}, hôm nay chúng ta sẽ tập trung vào quản lý khối lượng công việc và sức khỏe đội nhóm. Cân bằng hiệu suất với sức khỏe tinh thần rất quan trọng cho sự thành công của đội nhóm, và vai trò của bạn là tạo môi trường hỗ trợ thúc đẩy sự phát triển và khả năng phục hồi. Hãy cùng khám phá các chiến lược giao tiếp với đội nhóm, thiết lập ranh giới và hỗ trợ sức khỏe tinh thần.`,
            submodules: {
              practice_mental_health: {
                title:
                  'Tôi cần gợi ý để hướng dẫn các cuộc trò chuyện về sức khỏe tinh thần',
                description: 'Xử lý nhạy cảm các cuộc thảo luận về sức khỏe',
              },
            },
          },
        },
        namedialog: {
          title: 'Tên bạn là gì?',
          placeholder: 'Nhập tên của bạn',
          ctalabel: 'Bắt đầu phiên huấn luyện',
        },
        roleplay_cta: {
          practice: 'Thực hành',
          learn: 'Học hỏi',
          generating: 'Đang chọn mô-đun thực hành dựa trên cuộc trò chuyện...',
        },
        scenario_generation_messages: {
          message1:
            'Đang tạo kịch bản thực hành cá nhân dựa trên cuộc trò chuyện...',
          message2: 'Đang phân tích sở thích của bạn...',
          message3: 'Sắp xong, đang tinh chỉnh mọi thứ...',
          message4: 'Đang hoàn thiện kịch bản với các chi tiết tốt nhất...',
          message5: 'Chỉ một chút nữa thôi, đang chuẩn bị mọi thứ...',
        },
        network: {
          connection_quality: 'Chất lượng kết nối',
          latency: 'Độ trễ',
        },
        avatar: {
          speaking: 'Đang nói',
          listening: 'Đang nghe',
        },
        transcript: {
          ai_coach: 'AI Coach',
          you: 'Bạn',
        },
        error: {
          title: 'Lỗi kết nối',
          message:
            'Không thể kết nối với AI Coach. Vui lòng kiểm tra kết nối internet và thử lại.',
          try_again: 'Thử lại',
        },
        metrics: {
          title: 'Chỉ số phiên',
          speaking_time: 'Thời gian nói',
          response_speed: 'Tốc độ phản hồi',
          turn_taking: 'Lượt trao đổi',
        },
        loading: {
          title: {
            connecting: 'Đang Kết Nối AI Coach',
            ending: 'Đang Tạo Phản Hồi',
            loading: 'Đang Tải Kịch Bản',
            error: 'Lỗi',
          },
          message: {
            connecting: 'Đang thiết lập kết nối bảo mật...',
            ending: 'Đang phân tích hiệu suất của bạn...',
            loading: 'Đang chuẩn bị phiên thực hành cá nhân...',
            error: 'Lỗi',
          },
        },
        scenario: {
          phase: {
            pre_start: 'Chuẩn bị',
            intro: 'Giới thiệu',
            main: 'Cuộc trò chuyện chính',
          },
          details: {
            objective: 'Mục tiêu',
            framework: 'Khung phương pháp',
            no_framework: 'Không có khung phương pháp',
            view_details: 'Xem chi tiết khung phương pháp',
            title: 'Chi tiết kịch bản',
            summary: 'Tóm tắt',
          },
          buttons: {
            start: 'Bắt đầu thực hành',
            end: 'Kết thúc phiên',
          },
        },
        framework_modal: {
          close: 'Đóng',
        },
        roleplay_area: {
          scenario_section: {
            title: 'Kịch bản',
          },
          ai_coach: {
            alt: 'AI Coach',
          },
        },
        control_panel: {
          skip_intro: 'Bỏ qua giới thiệu',
          end_session: 'Kết thúc phiên',
          ai_status: {
            speaking: 'AI đang nói',
            listening: 'AI đang nghe',
          },
        },
        connection_monitor: {
          messages: {
            offline: 'Không có kết nối internet',
            good: 'Kết nối mạnh',
            fair: 'Kết nối trung bình - có thể có độ trễ',
            poor: 'Kết nối yếu - có thể có độ trễ',
            checking: 'Đang kiểm tra kết nối...',
          },
        },
      },
    },
  },
};

// Merge base resources with feedback and custom translations
export const resources = mergeTranslations(
  baseResources,
  feedbackTranslations,
  customTranslations,
);
